import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play, Star, Clock, TrendingUp, Sparkles, Crown, Globe, Wifi,
  X, List, Pause, SkipBack, SkipForward, Settings, Maximize,
  Search, ChevronLeft, ChevronRight, Home,
  ThumbsUp, Gem, Check
} from 'lucide-react';

const formatCount = (num = 0) => {
  if (num < 1000) return num.toString();
  if (num < 1_000_000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return (num / 1_000_000).toFixed(2).replace(/\.0+$/, '') + 'M';
};

// Drama Player Component
const DramaPlayer = ({ drama, onClose, initialEpisode = 1 }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentEpisode, setCurrentEpisode] = useState(initialEpisode);
  const [showEpisodes, setShowEpisodes] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState(0);
  const [episodesData, setEpisodesData] = useState([]);
  const [videoLanguage, setVideoLanguage] = useState('en');
  const [subtitleLanguage, setSubtitleLanguage] = useState('en');
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [subsEnabled, setSubsEnabled] = useState(true);
const [showSubtitleSettings, setShowSubtitleSettings] = useState(false);
const [currentSubtitle, setCurrentSubtitle] = useState('');
const [subSettings, setSubSettings] = useState({
  fontSize: 30,
  fontFamily: 'Comic Sans MS',
  color: '#ffffff',
  bgColor: 'rgba(0, 0, 0, 0.9)',
  bgStyle: 'box',
  position: 13, // percentage from bottom
  horizontalAlign: 'center',
  fontWeight: 'normal',
  textShadow: true,
  opacity: 0.7
});
const [isDragging, setIsDragging] = useState(false);
const [expandedSection, setExpandedSection] = useState(null);

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const lastTapRef = useRef(0);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const hasUserInteractedRef = useRef(false);
const progressBarRef = useRef(null);
const wasPlayingRef = useRef(false);

const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 1500);
    }
  }, [isPlaying]);

// Replace handleProgressInteraction and all drag handlers (around line 60-120) with:
const handleProgressStart = useCallback((e) => {
  if (!videoRef.current || isNaN(videoRef.current.duration)) return;
  
  e.preventDefault();
  setIsDragging(true);
  wasPlayingRef.current = !videoRef.current.paused;
  
  // Pause video during drag
  if (!videoRef.current.paused) {
    videoRef.current.pause();
  }
  
  const rect = progressBarRef.current.getBoundingClientRect();
  const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
  const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  
  videoRef.current.currentTime = pos * videoRef.current.duration;
}, []);

const handleProgressMove = useCallback((e) => {
  if (!isDragging || !videoRef.current || isNaN(videoRef.current.duration)) return;
  
  e.preventDefault();
  
  const rect = progressBarRef.current.getBoundingClientRect();
  const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
  const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  
  videoRef.current.currentTime = pos * videoRef.current.duration;
}, [isDragging]);

const handleProgressEnd = useCallback((e) => {
  if (!isDragging) return;
  
  e.preventDefault();
  setIsDragging(false);
  
  // Resume playback if it was playing before
  if (wasPlayingRef.current && videoRef.current) {
    videoRef.current.play().then(() => {
      setIsPlaying(true);
    }).catch(() => {
      setIsPlaying(false);
    });
  }
  
  resetControlsTimeout();
}, [isDragging, resetControlsTimeout]);

// Update the useEffect for drag listeners (around line 100):
useEffect(() => {
  if (!isDragging) return;
  
  const handleMove = (e) => handleProgressMove(e);
  const handleEnd = (e) => handleProgressEnd(e);
  
  // Add listeners to window for better tracking
  window.addEventListener('mousemove', handleMove, { passive: false });
  window.addEventListener('mouseup', handleEnd, { passive: false });
  window.addEventListener('touchmove', handleMove, { passive: false });
  window.addEventListener('touchend', handleEnd, { passive: false });
  
  return () => {
    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('mouseup', handleEnd);
    window.removeEventListener('touchmove', handleMove);
    window.removeEventListener('touchend', handleEnd);
  };
}, [isDragging, handleProgressMove, handleProgressEnd]);  


const togglePlay = useCallback(() => {
    if (videoRef.current?.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
    resetControlsTimeout();
  }, [resetControlsTimeout]);
  
const closeAllPanels = () => {
  setShowEpisodes(false);
  setShowSettings(false);
  setShowSubtitleSettings(false);
};  
  
// Load custom fonts
useEffect(() => {
  const link = document.createElement('link');
  link.href = '/fonts/fonts.css';
  link.rel = 'stylesheet';
  document.head.appendChild(link);
  
  return () => {
    document.head.removeChild(link);
  };
}, []); 

  // Load user preferences from localStorage
  useEffect(() => {
    const savedVideoLang = localStorage.getItem('videoLanguage');
    const savedSubLang = localStorage.getItem('subtitleLanguage');
    const savedSpeed = localStorage.getItem('playbackSpeed');
    
    if (savedVideoLang) setVideoLanguage(savedVideoLang);
    if (savedSubLang) setSubtitleLanguage(savedSubLang);
    if (savedSpeed) setPlaybackSpeed(parseFloat(savedSpeed));
  }, []);
  
// Load subtitle customization settings
useEffect(() => {
  const savedSubSettings = localStorage.getItem('subtitleSettings');
  if (savedSubSettings) {
    try {
      setSubSettings(JSON.parse(savedSubSettings));
    } catch (e) {
      console.error('Failed to load subtitle settings');
    }
  }
}, []);

  // Fetch episode data from JSON
  useEffect(() => {
    const fetchEpisodeData = async () => {
      try {
        const response = await fetch(`https://fandom.panguplay.workers.dev${drama.video_url}`);
        const data = await response.json();
        setEpisodesData(data.episodes || []);
      } catch (error) {
        console.error('Error fetching episode data:', error);
      }
    };
    fetchEpisodeData();
  }, [drama.video_url]);

const saveLastWatched = useCallback((episode, time) => {
  const watched = JSON.parse(localStorage.getItem('lastWatched') || '{}');
  watched[drama.id] = { episode, time, timestamp: Date.now() };
  localStorage.setItem('lastWatched', JSON.stringify(watched));
}, [drama.id]);

  const getCurrentEpisode = () => {
    return episodesData.find(ep => ep.position === currentEpisode);
  };

const getVideoUrl = useCallback((episode) => {
  if (!episode) return null;
  
  if (episode.videoUrls && episode.videoUrls[videoLanguage]) {
    return episode.videoUrls[videoLanguage];
  }
  if (episode.videoUrls && episode.videoUrls['en']) {
    return episode.videoUrls['en'];
  }
  return episode.videoUrl || null;
}, [videoLanguage]);

const getSubtitleUrl = useCallback((episode) => {
  if (!episode || !episode.subs) return null;
  
  if (episode.subs[subtitleLanguage]) {
    return episode.subs[subtitleLanguage];
  }
  if (episode.subs['en']) {
    return episode.subs['en'];
  }
  return null;
}, [subtitleLanguage]);

  const getAvailableVideoLanguages = (episode) => {
    if (!episode || !episode.videoUrls) return ['en'];
    return Object.keys(episode.videoUrls);
  };

  const getAvailableSubtitleLanguages = (episode) => {
    if (!episode || !episode.subs) return ['en'];
    return Object.keys(episode.subs);
  };

const applySubtitleSettings = useCallback((video) => {
  const tracks = video.textTracks;
  if (!tracks || !tracks[0]) return;

  const track = tracks[0];
  
  if (!subsEnabled) {
    track.mode = 'disabled';
    setCurrentSubtitle('');
    return;
  }
  
  track.mode = 'hidden';

  const updateSubtitle = () => {
    if (!track.activeCues || track.activeCues.length === 0) {
      setCurrentSubtitle('');
      return;
    }
    
    const cue = track.activeCues[0];
    setCurrentSubtitle(cue.text);
  };

  updateSubtitle();
  track.addEventListener('cuechange', updateSubtitle);
  
  return () => {
    track.removeEventListener('cuechange', updateSubtitle);
  };
}, [subsEnabled]);

const saveSubtitleSettings = (newSettings) => {
  setSubSettings(newSettings);
  localStorage.setItem('subtitleSettings', JSON.stringify(newSettings));
};

const loadEpisode = useCallback((episodeNum) => {
  // Check if episodesData is ready first
  if (!episodesData || episodesData.length === 0) {
    console.error('Episodes data not loaded yet');
    return;
  }

  setLoading(true);
  
  const episode = episodesData.find(ep => ep.position === episodeNum);
  
  // Stop loading if episode not found
  if (!episode) {
    console.error(`Episode ${episodeNum} not found`);
    setLoading(false);
    return;
  }

  const videoUrl = getVideoUrl(episode);
  const subUrl = getSubtitleUrl(episode);

  if (hlsRef.current) {
    hlsRef.current.destroy();
    hlsRef.current = null;
  }

  const video = videoRef.current;
  if (!video) {
    setLoading(false);
    return;
  }

  if (window.Hls && window.Hls.isSupported()) {
    const hls = new window.Hls();
    hls.loadSource(videoUrl);
    hls.attachMedia(video);

    hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
      setCurrentEpisode(episodeNum);
      saveLastWatched(episodeNum, 0);
      setLoading(false);
      video.playbackRate = playbackSpeed;
      video.play().then(() => {
        setIsPlaying(true);
        resetControlsTimeout();
      }).catch(() => {
        setIsPlaying(false);
      });
    });

    hls.on(window.Hls.Events.ERROR, (event, data) => {
      if (data.fatal) {
        console.error('HLS fatal error', data);
        setLoading(false);
      }
    });

    hlsRef.current = hls;
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = videoUrl;
    video.onloadedmetadata = () => {
      setCurrentEpisode(episodeNum);
      saveLastWatched(episodeNum, 0);
      setLoading(false);
      video.playbackRate = playbackSpeed;
      video.play().then(() => {
        setIsPlaying(true);
        resetControlsTimeout();
      }).catch(() => {
        setIsPlaying(false);
      });
    };
    
    video.onerror = () => {
      console.error('Video load error');
      setLoading(false);
    };
  }

  // Load subtitles
  if (subUrl) {
    const existingTracks = video.querySelectorAll('track');
    existingTracks.forEach(track => track.remove());
    const track = document.createElement('track');
    track.kind = 'subtitles';
    track.label = subtitleLanguage.toUpperCase();
    track.srclang = subtitleLanguage;
    track.src = subUrl;
    track.default = true;
    video.appendChild(track);
    applySubtitleSettings(video);
  }
}, [episodesData, getVideoUrl, getSubtitleUrl, saveLastWatched, playbackSpeed, resetControlsTimeout, subtitleLanguage, applySubtitleSettings]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
    script.async = true;
    script.onload = () => {
      if (episodesData.length > 0) {
        loadEpisode(initialEpisode);
        setTimeout(() => {
          if (containerRef.current && !document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(() => {});
          }
        }, 500);
      }
    };
    document.body.appendChild(script);
    return () => {
      if (hlsRef.current) hlsRef.current.destroy();
    };
  }, [episodesData, initialEpisode]);

  useEffect(() => {
    if (episodesData.length > 0 && videoRef.current) {
      loadEpisode(currentEpisode);
    }
  }, [videoLanguage, subtitleLanguage, currentEpisode, episodesData.length]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  useEffect(() => {
    setSelectedRange(Math.floor((currentEpisode - 1) / 20));
  }, [currentEpisode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch(e.key) {
        case 'ArrowLeft':
          if (videoRef.current && !isNaN(videoRef.current.duration)) {
  videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
  resetControlsTimeout();
}
          resetControlsTimeout();
          break;
        case 'ArrowRight':
          if (videoRef.current && !isNaN(videoRef.current.duration)) {
  videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 5);
  resetControlsTimeout();
}
          resetControlsTimeout();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (currentEpisode < drama.total_episodes) loadEpisode(currentEpisode + 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (currentEpisode > 1) loadEpisode(currentEpisode - 1);
          break;
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'f':
        case 'F':
        case 'Enter':
          if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen();
          } else {
            document.exitFullscreen();
          }
          break;
		case 'n':
      case 'N':
        e.preventDefault();
        if (currentEpisode < drama.total_episodes) loadEpisode(currentEpisode + 1);
        break;
      case 'p':
      case 'P':
        e.preventDefault();
        if (currentEpisode > 1) loadEpisode(currentEpisode - 1);
        break;
case 's':
case 'S':
  e.preventDefault();
  if (showSettings) {
    setShowSettings(false);  // If already open, just close it
  } else {
    closeAllPanels();        // Close others first
    setShowSettings(true);   // Then open this one
  }
  break;

case 'l':
case 'L':
  e.preventDefault();
  if (showEpisodes) {
    setShowEpisodes(false);
  } else {
    closeAllPanels();
    setShowEpisodes(true);
  }
  break;

case 't':
case 'T':
  e.preventDefault();
  if (showSubtitleSettings) {
    setShowSubtitleSettings(false);
  } else {
    closeAllPanels();
    setShowSubtitleSettings(true);
  }
  break;
default:
    break;  
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
}, [currentEpisode, drama.total_episodes, loadEpisode, resetControlsTimeout, togglePlay, showEpisodes, showSettings, showSubtitleSettings]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setProgress((video.currentTime / video.duration) * 100);
      if (video.currentTime > 0) {
        saveLastWatched(currentEpisode, video.currentTime);
      }
    };
    const handleLoadedMetadata = () => setDuration(video.duration);
const handleEnded = () => {
  if (currentEpisode < drama.total_episodes) {
    const nextEpisode = episodesData.find(ep => ep.position === currentEpisode + 1);
    if (nextEpisode) {
      loadEpisode(currentEpisode + 1);
    }
  }
};
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
    };
  }, [currentEpisode, drama.total_episodes, episodesData, loadEpisode]);

  const handleVideoClick = (e) => {
    if (!hasUserInteractedRef.current) {
      hasUserInteractedRef.current = true;
      videoRef.current.muted = false;
    }

    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;
    
    if (timeSinceLastTap < 300) {
      const rect = videoRef.current.getBoundingClientRect();
      const x = e.clientX || e.touches?.[0]?.clientX || 0;
      const isLeftSide = x < rect.width / 2;
      
      if (isLeftSide) {
  if (videoRef.current && !isNaN(videoRef.current.duration)) {
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
  }
} else {
  if (videoRef.current && !isNaN(videoRef.current.duration)) {
    videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 5);
  }
}
    } else {
      resetControlsTimeout();
    }
    lastTapRef.current = now;
  };

  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  const handleTouchEnd = (e) => {
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    
    if (Math.abs(deltaY) > 50) {
      if (deltaY > 0 && currentEpisode > 1) {
        loadEpisode(currentEpisode - 1);
      } else if (deltaY < 0 && currentEpisode < drama.total_episodes) {
        loadEpisode(currentEpisode + 1);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getEpisodeRanges = () => {
    const ranges = [];
    for (let i = 0; i < drama.total_episodes; i += 20) {
      const start = i + 1;
      const end = Math.min(i + 20, drama.total_episodes);
      ranges.push({ start, end, label: `${start}-${end}` });
    }
    return ranges;
  };

  const getCurrentRangeEpisodes = () => {
    const ranges = getEpisodeRanges();
    const range = ranges[selectedRange];
    if (!range) return [];
    const episodes = [];
    for (let i = range.start; i <= range.end; i++) {
      episodes.push(i);
    }
    return episodes;
  };

  const handleVideoLanguageChange = (lang) => {
    setVideoLanguage(lang);
    localStorage.setItem('videoLanguage', lang);
  };

  const handleSubtitleLanguageChange = (lang) => {
    setSubtitleLanguage(lang);
    localStorage.setItem('subtitleLanguage', lang);
  };

  const handlePlaybackSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    localStorage.setItem('playbackSpeed', speed.toString());
  };

  const episodeRanges = getEpisodeRanges();
  const currentRangeEpisodes = getCurrentRangeEpisodes();
  const currentEpisodeData = getCurrentEpisode();
  const availableVideoLangs = currentEpisodeData ? getAvailableVideoLanguages(currentEpisodeData) : ['en'];
  const availableSubLangs = currentEpisodeData ? getAvailableSubtitleLanguages(currentEpisodeData) : ['en'];

// Auto-hide cursor during playback
useEffect(() => {
  const container = containerRef.current;
  if (!container) return;
  
  let timeoutId;
  
  const hideCursor = () => {
    if (isPlaying && !showControls) {
      container.style.cursor = 'none';
    }
  };
  
  const showCursor = () => {
    container.style.cursor = 'default';
    clearTimeout(timeoutId);
    
    if (isPlaying) {
      timeoutId = setTimeout(hideCursor, 1000);
    }
  };
  
  container.addEventListener('mousemove', showCursor);
  
  // Initialize cursor state
  if (isPlaying && !showControls) {
    timeoutId = setTimeout(hideCursor, 1000);
  } else {
    container.style.cursor = 'default';
  }
  
  return () => {
    container.removeEventListener('mousemove', showCursor);
    container.style.cursor = 'default';
    clearTimeout(timeoutId);
  };
}, [isPlaying, showControls]);

  return (
    <div ref={containerRef} className="fixed inset-0 bg-black z-50 flex flex-col" onMouseMove={resetControlsTimeout} onClick={(e) => {
      if (e.target === e.currentTarget) resetControlsTimeout();
    }}>
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video 
          ref={videoRef} 
          className="w-full h-full object-contain" 
          onClick={handleVideoClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          playsInline 
          crossOrigin="anonymous" 
        />
		
{/* CUSTOM SUBTITLE OVERLAY */}
{subsEnabled && currentSubtitle && (
  <div 
    className="absolute left-0 right-0 flex items-center justify-center px-4 pointer-events-none z-10"
    style={{
      bottom: `${subSettings.position}%`,
      justifyContent: subSettings.horizontalAlign === 'left' ? 'flex-start' : 
                      subSettings.horizontalAlign === 'right' ? 'flex-end' : 'center'
    }}
  >
    <div
      className="max-w-[90%] md:max-w-[80%] transition-opacity duration-200"
      style={{
        fontSize: `${subSettings.fontSize}px`,
        fontFamily: subSettings.fontFamily,
        fontWeight: subSettings.fontWeight,
        color: subSettings.color,
        textAlign: 'center',
        lineHeight: '1.4',
        opacity: subSettings.opacity,
        padding: subSettings.bgStyle === 'box' ? '0.3em 0.6em' : '0.2em 0.4em',
        borderRadius: subSettings.bgStyle === 'box' ? '0.35em' : '0',
        background: subSettings.bgStyle === 'box' ? subSettings.bgColor : 
                   subSettings.bgStyle === 'shadow' ? 'transparent' : 
                   subSettings.bgStyle === 'outline' ? 'transparent' : 'transparent',
        textShadow: subSettings.textShadow 
          ? (subSettings.bgStyle === 'outline' 
              ? '-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000, -2px 0 0 #000, 2px 0 0 #000, 0 -2px 0 #000, 0 2px 0 #000'
              : subSettings.bgStyle === 'shadow'
                ? '0 4px 12px rgba(0,0,0,1), 0 2px 6px rgba(0,0,0,0.8)'
                : '0 2px 4px rgba(0,0,0,0.9)')
          : 'none',
        WebkitTextStroke: subSettings.bgStyle === 'outline' ? '1px #000' : 'none'
      }}
      dangerouslySetInnerHTML={{ __html: currentSubtitle }}
    />
  </div>
)}		
		
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
            <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        <div className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/90 via-black/50 to-transparent p-4 transition-opacity duration-300 z-30 pointer-events-none ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center gap-4 pointer-events-auto">
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center backdrop-blur-sm border border-white/30">
              <X className="w-6 h-6 text-white" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-white drop-shadow-lg">{drama.title}</h2>
              <p className="text-sm text-gray-200 drop-shadow-lg">Episode {currentEpisode} of {drama.total_episodes}</p>
            </div>
          </div>
        </div>
        <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 pb-6 transition-opacity duration-300 z-30 pointer-events-none ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          <div className="pointer-events-auto">
            <div className="w-full h-1 bg-white/20 rounded-full mb-4 cursor-pointer group" onClick={(e) => {
  if (videoRef.current && !isNaN(videoRef.current.duration)) {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pos * videoRef.current.duration;
  }
}}>


<div 
  ref={progressBarRef}
  className="w-full h-1 bg-white/20 rounded-full mb-4 cursor-pointer group relative"
  onMouseDown={handleProgressStart}
  onTouchStart={handleProgressStart}
>
  <div 
    className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full relative transition-none" 
    style={{ width: `${progress}%` }}
  >
    <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg transition-opacity ${isDragging ? 'opacity-100 scale-125' : 'opacity-0 group-hover:opacity-100'}`} />
  </div>
  
  {/* Hover time preview */}
  <div 
    className="absolute top-0 left-0 right-0 h-2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
    style={{ display: isDragging ? 'block' : 'none' }}
  >
    <div 
      className="absolute bg-white text-black text-xs px-2 py-1 rounded -translate-x-1/2 -translate-y-full mb-2 whitespace-nowrap"
      style={{ left: `${progress}%` }}
    >
      {formatTime(currentTime)}
    </div>
  </div>
</div>
            </div>
            <div className="flex items-center justify-between gap-2 md:gap-4">
              <div className="flex items-center gap-1 md:gap-2">
                <button onClick={togglePlay} className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/30 hover:bg-white/40 flex items-center justify-center backdrop-blur-sm border border-white/40 shadow-lg">
                  {isPlaying ? <Pause className="w-5 h-5 md:w-6 md:h-6 text-white" fill="white" /> : <Play className="w-5 h-5 md:w-6 md:h-6 text-white ml-0.5" fill="white" />}
                </button>
                <button onClick={() => currentEpisode > 1 && loadEpisode(currentEpisode - 1)} disabled={currentEpisode === 1} className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/25 hover:bg-white/35 flex items-center justify-center backdrop-blur-sm border border-white/30 shadow-lg disabled:opacity-30 disabled:cursor-not-allowed">
                  <SkipBack className="w-4 h-4 md:w-5 md:h-5 text-white" fill="white" />
                </button>
                <button onClick={() => currentEpisode < drama.total_episodes && loadEpisode(currentEpisode + 1)} disabled={currentEpisode === drama.total_episodes} className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/25 hover:bg-white/35 flex items-center justify-center backdrop-blur-sm border border-white/30 shadow-lg disabled:opacity-30 disabled:cursor-not-allowed">
                  <SkipForward className="w-4 h-4 md:w-5 md:h-5 text-white" fill="white" />
                </button>
                <span className="hidden md:block text-xs md:text-sm text-white font-medium ml-1 md:ml-2 drop-shadow-lg">{formatTime(currentTime)} / {formatTime(duration)}</span>
              </div>
              <div className="flex items-center gap-1 md:gap-2">
                <button onClick={() => {closeAllPanels(); setShowEpisodes(true)}} className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/25 hover:bg-white/35 flex items-center justify-center backdrop-blur-sm border border-white/30 shadow-lg">
                  <List className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </button>
<button 
  onClick={() => {closeAllPanels(); setShowSubtitleSettings(true);}} 
  className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center backdrop-blur-sm border shadow-lg ${
    showSubtitleSettings 
      ? 'bg-purple-600 border-purple-500' 
      : 'bg-white/25 hover:bg-white/35 border-white/30'
  }`}
>
  <span className="text-white text-xs md:text-sm font-bold">CC</span>
</button>
                <button onClick={() => {closeAllPanels(); setShowSettings(true);}} className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/25 hover:bg-white/35 flex items-center justify-center backdrop-blur-sm border border-white/30 shadow-lg">
                  <Settings className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </button>
                <button onClick={() => { if (!document.fullscreenElement) containerRef.current.requestFullscreen(); else document.exitFullscreen(); }} className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/25 hover:bg-white/35 flex items-center justify-center backdrop-blur-sm border border-white/30 shadow-lg">
                  <Maximize className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {showSettings && (
  <>
    <div 
      className="fixed inset-0 z-39" 
      onClick={() => setShowSettings(false)}
    />
    <div 
      className="absolute bottom-20 right-4 bg-black/95 backdrop-blur-xl rounded-2xl p-4 min-w-[280px] z-40 border border-white/10"
      onClick={(e) => e.stopPropagation()}
    >
            <div className="space-y-4">
              <div>
                <label className="text-white text-sm font-semibold mb-2 block">Video Language</label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {availableVideoLangs.map(lang => (
                    <button
                      key={lang}
                      onClick={() => handleVideoLanguageChange(lang)}
                      className={`w-full px-3 py-2 rounded-lg text-left text-sm transition-colors flex items-center justify-between ${
                        videoLanguage === lang ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      <span>{lang.toUpperCase()}</span>
                      {videoLanguage === lang && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-white text-sm font-semibold mb-2 block flex items-center justify-between">
                  <span>Subtitles</span>
                  <button
                    onClick={() => setSubsEnabled(!subsEnabled)}
                    className={`text-xs px-2 py-1 rounded ${subsEnabled ? 'bg-purple-600' : 'bg-gray-600'}`}
                  >
                    {subsEnabled ? 'ON' : 'OFF'}
                  </button>
                </label>
                {subsEnabled && (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {availableSubLangs.map(lang => (
                      <button
                        key={lang}
                        onClick={() => handleSubtitleLanguageChange(lang)}
                        className={`w-full px-3 py-2 rounded-lg text-left text-sm transition-colors flex items-center justify-between ${
                          subtitleLanguage === lang ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >
                        <span>{lang.toUpperCase()}</span>
                        {subtitleLanguage === lang && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-white text-sm font-semibold mb-2 block">Playback Speed</label>
                <div className="grid grid-cols-3 gap-2">
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map(speed => (
                    <button
                      key={speed}
                      onClick={() => handlePlaybackSpeedChange(speed)}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                        playbackSpeed === speed ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
		  </>
        )}

{showSubtitleSettings && (
  <>
    <div 
      className="fixed inset-0 z-39" 
      onClick={() => setShowSubtitleSettings(false)}
    />
    <div 
      className="absolute bottom-20 right-4 bg-black/95 backdrop-blur-xl rounded-2xl p-4 w-[320px] max-h-[75vh] overflow-y-auto z-40 border border-white/10 scrollbar-hide"
      onClick={(e) => e.stopPropagation()}
    >
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-white font-bold">Subtitle Settings</h3>
      <button
        onClick={() => setShowSubtitleSettings(false)}
        className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
      >
        <X className="w-4 h-4 text-white" />
      </button>
    </div>

    <div className="space-y-4">
      {/* Subtitle Toggle */}
      <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
        <span className="text-white text-sm font-semibold">Show Subtitles</span>
        <button
          onClick={() => setSubsEnabled(!subsEnabled)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold ${
            subsEnabled ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-300'
          }`}
        >
          {subsEnabled ? 'ON' : 'OFF'}
        </button>
      </div>

      {subsEnabled && (
        <>
          {/* Font Size */}
          <div>
            <label className="text-white text-sm mb-2 block flex items-center justify-between">
              <span>Font Size</span>
              <span className="text-purple-400 text-xs">{subSettings.fontSize}px</span>
            </label>
            <input
              type="range"
              min="16"
              max="48"
              step="2"
              value={subSettings.fontSize}
              onChange={(e) => saveSubtitleSettings({...subSettings, fontSize: parseInt(e.target.value)})}
              className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Small</span>
              <span>Large</span>
            </div>
          </div>

          {/* Font Family */}
          <div>
            <label className="text-white text-sm mb-2 block">Font Family</label>
<select
  value={subSettings.fontFamily}
  onChange={(e) => saveSubtitleSettings({...subSettings, fontFamily: e.target.value})}
  className="w-full px-3 py-2 bg-white/10 text-white text-sm rounded-lg border border-white/20 focus:outline-none focus:border-purple-600"
  style={{ colorScheme: 'dark' }}
>
  <option value="Arial" style={{backgroundColor: '#1f2937', color: '#fff'}}>Arial</option>
  <option value="Verdana" style={{backgroundColor: '#1f2937', color: '#fff'}}>Verdana</option>
  <option value="Georgia" style={{backgroundColor: '#1f2937', color: '#fff'}}>Georgia</option>
  <option value="Times New Roman" style={{backgroundColor: '#1f2937', color: '#fff'}}>Times New Roman</option>
  <option value="Courier New" style={{backgroundColor: '#1f2937', color: '#fff'}}>Courier New</option>
  <option value="Comic Sans MS" style={{backgroundColor: '#1f2937', color: '#fff'}}>Comic Sans MS</option>
  <option value="Impact" style={{backgroundColor: '#1f2937', color: '#fff'}}>Impact</option>
  <option value="Tahoma" style={{backgroundColor: '#1f2937', color: '#fff'}}>Tahoma</option>
</select>
          </div>

          {/* Font Weight */}
          <div>
            <label className="text-white text-sm mb-2 block">Font Weight</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => saveSubtitleSettings({...subSettings, fontWeight: 'normal'})}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  subSettings.fontWeight === 'normal' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                Normal
              </button>
              <button
                onClick={() => saveSubtitleSettings({...subSettings, fontWeight: 'bold'})}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  subSettings.fontWeight === 'bold' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                Bold
              </button>
            </div>
          </div>

          {/* Text Color */}
          <div>
            <label className="text-white text-sm mb-2 block">Text Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={subSettings.color}
                onChange={(e) => saveSubtitleSettings({...subSettings, color: e.target.value})}
                className="w-16 h-10 rounded cursor-pointer bg-transparent"
              />
              <span className="text-white text-sm font-mono">{subSettings.color}</span>
            </div>
          </div>

          {/* Background Style */}
          <div>
            <label className="text-white text-sm mb-2 block">Background Style</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'box', label: 'Box' },
                { value: 'shadow', label: 'Shadow' },
                { value: 'outline', label: 'Outline' },
                { value: 'none', label: 'None' }
              ].map(style => (
                <button
                  key={style.value}
                  onClick={() => saveSubtitleSettings({...subSettings, bgStyle: style.value})}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    subSettings.bgStyle === style.value ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {style.label}
                </button>
              ))}
            </div>
          </div>

          {/* Background Color (only for box style) */}
          {subSettings.bgStyle === 'box' && (
            <div>
              <label className="text-white text-sm mb-2 block">Background Color</label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={(() => {
                      const match = subSettings.bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                      if (match) {
                        const [, r, g, b] = match;
                        return `#${[r, g, b].map(x => parseInt(x).toString(16).padStart(2, '0')).join('')}`;
                      }
                      return '#000000';
                    })()}
                    onChange={(e) => {
                      const hex = e.target.value;
                      const r = parseInt(hex.slice(1, 3), 16);
                      const g = parseInt(hex.slice(3, 5), 16);
                      const b = parseInt(hex.slice(5, 7), 16);
                      const opacity = parseFloat(subSettings.bgColor.match(/[\d.]+\)$/)?.[0] || '0.8');
                      saveSubtitleSettings({...subSettings, bgColor: `rgba(${r}, ${g}, ${b}, ${opacity})`});
                    }}
                    className="w-16 h-10 rounded cursor-pointer bg-transparent"
                  />
                  <div className="flex-1">
                    <div className="text-xs text-gray-400 mb-1">Opacity: {Math.round((parseFloat(subSettings.bgColor.match(/[\d.]+\)$/)?.[0] || '0.8') * 100))}%</div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={parseFloat(subSettings.bgColor.match(/[\d.]+\)$/)?.[0] || '0.8')}
                      onChange={(e) => {
                        const rgb = subSettings.bgColor.match(/\d+/g);
                        saveSubtitleSettings({
                          ...subSettings, 
                          bgColor: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${e.target.value})`
                        });
                      }}
                      className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Text Shadow */}
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <span className="text-white text-sm">Text Shadow</span>
            <button
              onClick={() => saveSubtitleSettings({...subSettings, textShadow: !subSettings.textShadow})}
              className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                subSettings.textShadow ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-300'
              }`}
            >
              {subSettings.textShadow ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* Opacity */}
          <div>
            <label className="text-white text-sm mb-2 block flex items-center justify-between">
              <span>Subtitle Opacity</span>
              <span className="text-purple-400 text-xs">{Math.round(subSettings.opacity * 100)}%</span>
            </label>
            <input
              type="range"
              min="0.3"
              max="1"
              step="0.1"
              value={subSettings.opacity}
              onChange={(e) => saveSubtitleSettings({...subSettings, opacity: parseFloat(e.target.value)})}
              className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
          </div>

          {/* Vertical Position */}
          <div>
            <label className="text-white text-sm mb-2 block flex items-center justify-between">
              <span>Vertical Position</span>
              <span className="text-purple-400 text-xs">{subSettings.position}%</span>
            </label>
            <input
              type="range"
              min="5"
              max="30"
              step="1"
              value={subSettings.position}
              onChange={(e) => saveSubtitleSettings({...subSettings, position: parseInt(e.target.value)})}
              className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Bottom</span>
              <span>Top</span>
            </div>
          </div>

          {/* Horizontal Alignment */}
          <div>
            <label className="text-white text-sm mb-2 block">Horizontal Alignment</label>
            <div className="grid grid-cols-3 gap-2">
              {['left', 'center', 'right'].map(align => (
                <button
                  key={align}
                  onClick={() => saveSubtitleSettings({...subSettings, horizontalAlign: align})}
                  className={`px-3 py-2 rounded-lg text-sm capitalize transition-colors ${
                    subSettings.horizontalAlign === align ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {align}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="p-3 bg-white/5 rounded-lg">
            <div className="text-white text-xs mb-2">Preview:</div>
            <div 
              className="w-full flex items-center justify-center py-4 bg-gradient-to-b from-gray-800 to-gray-900 rounded"
              style={{
                justifyContent: subSettings.horizontalAlign === 'left' ? 'flex-start' : 
                                subSettings.horizontalAlign === 'right' ? 'flex-end' : 'center'
              }}
            >
              <div
                style={{
                  fontSize: `${Math.max(subSettings.fontSize * 0.5, 12)}px`,
                  fontFamily: subSettings.fontFamily,
                  fontWeight: subSettings.fontWeight,
                  color: subSettings.color,
                  textAlign: 'center',
                  lineHeight: '1.4',
                  opacity: subSettings.opacity,
                  padding: subSettings.bgStyle === 'box' ? '0.3em 0.6em' : '0.2em 0.4em',
                  borderRadius: subSettings.bgStyle === 'box' ? '0.35em' : '0',
                  background: subSettings.bgStyle === 'box' ? subSettings.bgColor : 'transparent',
                  textShadow: subSettings.textShadow 
                    ? (subSettings.bgStyle === 'outline' 
                        ? '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                        : subSettings.bgStyle === 'shadow'
                          ? '0 2px 6px rgba(0,0,0,1)'
                          : '0 1px 2px rgba(0,0,0,0.9)')
                    : 'none',
                  WebkitTextStroke: subSettings.bgStyle === 'outline' ? '0.5px #000' : 'none',
                  margin: '0 1em'
                }}
              >
                Sample Subtitle Text
              </div>
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={() => {
              const defaultSettings = {
                fontSize: 30,
                fontFamily: 'Comic Sans MS',
                color: '#ffffff',
                bgColor: 'rgba(0, 0, 0, 0.9)',
                bgStyle: 'box',
                position: 13,
                horizontalAlign: 'center',
                fontWeight: 'normal',
                textShadow: true,
                opacity: 0.7
              };
              saveSubtitleSettings(defaultSettings);
            }}
            className="w-full px-4 py-2.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm font-semibold transition-colors"
          >
            Reset to Default
          </button>
        </>
      )}
    </div>
  </div>
  </>
)}
		
      </div>

      {showEpisodes && (
  <div 
    className="fixed inset-0 z-40 bg-black/50" 
    onClick={() => setShowEpisodes(false)}
  >
    <div 
      className="fixed inset-x-0 bottom-0 h-[65vh] md:inset-y-0 md:right-0 md:left-auto md:h-full md:w-[480px] bg-black/95 backdrop-blur-xl flex flex-col animate-slide-up"
      onClick={(e) => e.stopPropagation()}
    >
          <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-black/60">
            <img src={drama.portrait} alt={drama.title} className="w-12 h-18 object-cover rounded-lg shadow-lg" />
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-white truncate">{drama.title}</h2>
              <p className="text-gray-400 text-xs">{drama.total_episodes} Episodes</p>
            </div>
            <button 
              onClick={() => setShowEpisodes(false)} 
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors flex-shrink-0"
            >
              Close
            </button>
          </div>
          
          <div className="flex gap-2 px-4 py-4 border-b border-white/10 overflow-x-auto scrollbar-hide">
            {episodeRanges.map((range, index) => (
              <button
                key={index}
                onClick={() => setSelectedRange(index)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  selectedRange === index
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="grid grid-cols-5 gap-2 max-w-md mx-auto">
              {currentRangeEpisodes.map((ep) => (
                <button 
                  key={ep} 
                  onClick={() => { loadEpisode(ep); setShowEpisodes(false); }} 
                  className="relative aspect-square rounded-lg font-medium transition-all flex items-center justify-center text-sm bg-white/5 hover:bg-white/10 text-white"
                >
                  {ep === currentEpisode && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full border-2 border-blue-500 animate-pulse-ring"></div>
                    </div>
                  )}
                  <span className={ep === currentEpisode ? 'relative z-10 text-white font-semibold' : ''}>
                    {ep}
                  </span>
                </button>
              ))}
            </div>
          </div>
		  </div>
        </div>
      )}
    </div>
  );
};

const DramaCard = ({ drama, onPlay, variant = 'horizontal' }) => {
  const getLastWatched = () => {
    const watched = JSON.parse(localStorage.getItem('lastWatched') || '{}');
    return watched[drama.id];
  };

  const lastWatched = getLastWatched();

  return (
    <div className={`group relative ${variant === 'grid' ? 'w-full' : 'flex-shrink-0 w-36 sm:w-40 md:w-44'}`}>
      <div className="aspect-[2/3] rounded-xl overflow-hidden bg-gradient-to-br from-purple-900 to-indigo-900 shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
        <img 
          src={drama.portrait} 
          alt={drama.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=400&h=600&fit=crop';
          }}
        />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
        
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button 
            onClick={() => onPlay(drama, lastWatched?.episode || 1)}
            className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 flex items-center justify-center transform hover:scale-110 transition-transform shadow-lg"
          >
            <Play className="w-8 h-8 text-white ml-1" fill="white" />
          </button>
        </div>

        {lastWatched && (
          <div className="absolute top-2 right-2 bg-purple-600 text-white px-2 py-1 rounded text-xs font-bold">
            Ep {lastWatched.episode}
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
          <h3 className="font-bold text-sm mb-1 line-clamp-2">{drama.title}</h3>
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{drama.total_episodes} Eps</span>
            </div>
            {drama.rating && (
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-400" fill="currentColor" />
                <span>{drama.rating}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CategorySection = ({ categoryName, dramas, onPlay, icon, expandedSection, setExpandedSection }) => {
  const scrollRef = useRef(null);

  const showAll = expandedSection === categoryName;

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Hide this section entirely if another section is expanded
  if (expandedSection !== null && expandedSection !== categoryName) {
    return null;
  }

  return (
    <div className="mb-12">

      {/* Fixed bar — sits just below the main header when grid is open */}
      {showAll && (
        <div className="fixed top-[64px] left-0 right-0 z-30 flex items-center justify-between px-4 py-3
          bg-black/95 backdrop-blur-md border-b border-white/10 shadow-lg">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            {icon}
            {categoryName}
            <span className="text-sm font-normal text-gray-400">{dramas.length} titles</span>
          </h2>
          <button
            onClick={() => setExpandedSection(null)}
            className="text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
          >
            Show Less
            <ChevronRight className="w-4 h-4 rotate-90 transition-transform duration-200" />
          </button>
        </div>
      )}

      {/* Normal section header — only shown in scroll mode */}
      {!showAll && (
        <div className="flex items-center justify-between px-4 mb-4">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            {icon}
            {categoryName}
          </h2>
          <button
            onClick={() => setExpandedSection(categoryName)}
            className="text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
          >
            View All ({dramas.length})
            <ChevronRight className="w-4 h-4 transition-transform duration-200" />
          </button>
        </div>
      )}

      {showAll ? (
        // Grid view — pt-16 so cards don't hide under the fixed bar
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 px-4 pt-16">
          {dramas.map((drama) => (
            <DramaCard key={drama.id} drama={drama} onPlay={onPlay} variant="grid" />
          ))}
        </div>
      ) : (
        // Horizontal scroll row
        <div className="relative group/row">
          <button
            onClick={() => scroll('left')}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/70 hover:bg-black/90 rounded-full items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide px-4 pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {dramas.map((drama) => (
              <DramaCard key={drama.id} drama={drama} onPlay={onPlay} />
            ))}
          </div>
          <button
            onClick={() => scroll('right')}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/70 hover:bg-black/90 rounded-full items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>
      )}
    </div>
  );
};

const MoreSeriesGrid = ({ dramas, onPlay }) => {
  const ITEMS_PER_BATCH = 12;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_BATCH);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + ITEMS_PER_BATCH, dramas.length));
        }
      },
      { rootMargin: '200px' }
    );

    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [dramas.length]);

  return (
    <div className="mb-16 px-4">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
        <Wifi className="w-6 h-6 text-blue-400" />
        More Series
      </h2>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
        {dramas.slice(0, visibleCount).map((drama) => (
          <div key={drama.id}>
            <DramaCard drama={drama} onPlay={onPlay} variant="grid" />
          </div>
        ))}
      </div>

      {visibleCount < dramas.length && (
        <div ref={loadMoreRef} className="h-12 flex items-center justify-center text-gray-400">
          Loading more...
        </div>
      )}
    </div>
  );
};

const ContinueWatchingSection = ({ dramas, onPlay }) => {
  const scrollRef = useRef(null);
  const [continueWatching, setContinueWatching] = useState([]);

  const loadContinueWatching = useCallback(() => {
    const watched = JSON.parse(localStorage.getItem('lastWatched') || '{}');
    const watchedDramas = Object.entries(watched)
      .map(([dramaId, data]) => {
        const drama = dramas.find(d => d.id === parseInt(dramaId));
        if (!drama) return null;
        return { ...drama, watchData: data };
      })
      .filter(Boolean)
      .filter(drama => drama.watchData.episode < drama.total_episodes) // ✅ Filter out completed
      .sort((a, b) => b.watchData.timestamp - a.watchData.timestamp)
      .slice(0, 10);
    
    setContinueWatching(watchedDramas);
  }, [dramas]);

  useEffect(() => {
    loadContinueWatching();
  }, [loadContinueWatching]);

  const handleRemove = useCallback((dramaId, e) => {
    e.stopPropagation(); // Prevent card click
    const watched = JSON.parse(localStorage.getItem('lastWatched') || '{}');
    delete watched[dramaId];
    localStorage.setItem('lastWatched', JSON.stringify(watched));
    loadContinueWatching(); // Reload the list
  }, [loadContinueWatching]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  if (continueWatching.length === 0) return null;

  return (
    <div className="mb-12 relative px-4">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
        <Clock className="w-6 h-6 text-blue-400" />
        Continue Watching
      </h2>
      <div className="relative group/row">
        <button
          onClick={() => scroll('left')}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/70 hover:bg-black/90 rounded-full items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <div ref={scrollRef} className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
          {continueWatching.map((drama) => (
            <div key={drama.id} className="flex-shrink-0 w-72">
              <div className="group relative bg-gradient-to-br from-purple-900/20 to-indigo-900/20 rounded-xl overflow-hidden hover:scale-105 transition-transform">
                {/* ✅ Close button */}
                <button
                  onClick={(e) => handleRemove(drama.id, e)}
                  className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove from continue watching"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                
                <div className="flex gap-3 p-3">
                  <div className="relative w-24 h-36 flex-shrink-0">
                    <img 
                      src={drama.portrait} 
                      alt={drama.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-8 h-8 text-white" fill="white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm mb-1 line-clamp-2">{drama.title}</h3>
                    <p className="text-gray-400 text-xs mb-2">Episode {drama.watchData.episode} of {drama.total_episodes}</p>
                    <div className="w-full bg-white/20 rounded-full h-1 mb-1">
                      <div 
                        className="bg-gradient-to-r from-purple-600 to-pink-600 h-full rounded-full"
                        style={{ width: `${(drama.watchData.episode / drama.total_episodes) * 100}%` }}
                      />
                    </div>
                    <p className="text-gray-500 text-xs">
                      {Math.round((drama.watchData.episode / drama.total_episodes) * 100)}% complete
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => onPlay(drama, drama.watchData.episode)}
                  className="absolute inset-0 w-full h-full"
                  aria-label={`Continue watching ${drama.title}`}
                />
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={() => scroll('right')}
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/70 hover:bg-black/90 rounded-full items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>
    </div>
  );
};

const HeroSection = ({ featuredDramas, onPlay }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(0);
  
  useEffect(() => {
    featuredDramas.forEach((d) => {
      const img = new Image();
      img.src = d.landscape || d.portrait;
    });
  }, [featuredDramas]);

  useEffect(() => {
  if (!featuredDramas || featuredDramas.length <= 1) return;
  const interval = setInterval(() => {
    setCurrentIndex((prevCurrentIndex) => {
      setPrevIndex(prevCurrentIndex);  // ✅ Set previous before updating current
      return (prevCurrentIndex + 1) % featuredDramas.length;
    });
  }, 5000);
  return () => clearInterval(interval);
}, [featuredDramas.length, featuredDramas]);

  if (!featuredDramas || featuredDramas.length === 0) return null;

  const featuredDrama = featuredDramas[currentIndex];

  const getLastWatched = () => {
    const watched = JSON.parse(localStorage.getItem('lastWatched') || '{}');
    return watched[featuredDrama.id];
  };

  const lastWatched = getLastWatched();

  return (
    <div className="relative h-[70vh] mb-12 rounded-3xl overflow-hidden mx-4">
<div className="absolute inset-0">
  {/* Previous image */}
  <div
    className="absolute inset-0 bg-center bg-cover transition-opacity duration-700"
    style={{
      backgroundImage: `url(${
        featuredDramas[prevIndex]?.landscape ||
        featuredDramas[prevIndex]?.portrait
      })`,
      opacity: 0
    }}
  />

  {/* Current image */}
  <div
    className="absolute inset-0 bg-center bg-cover transition-opacity duration-700"
    style={{
      backgroundImage: `url(${
        featuredDrama.landscape || featuredDrama.portrait
      })`,
      opacity: 1
    }}
  />

  <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
</div>

      <div className="relative h-full flex items-center px-8 md:px-16">
        <div className="max-w-2xl space-y-6">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold">
            <span className="animate-pulse">🔥</span> FEATURED
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
            {featuredDrama.title}
          </h1>

          <p className="text-xl text-gray-300 line-clamp-3">
            {featuredDrama.description}
          </p>

          <div className="flex gap-4 text-gray-300 flex-wrap">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{featuredDrama.total_episodes} Episodes</span>
            </div>
            {featuredDrama.rating && (
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                <span>{featuredDrama.rating}</span>
              </div>
            )}
			{/*
            {featuredDrama.view && (
  <div className="flex items-center gap-2">
    <Eye className="w-5 h-5" />
    <span>{formatCount(featuredDrama.view)} views</span>
  </div>
)}
*/}
{featuredDrama.likes && (
  <div className="flex items-center gap-2">
    <ThumbsUp className="w-5 h-5" />
    <span>{formatCount(featuredDrama.likes)} likes</span>
  </div>
)}
          </div>

          <div className="flex gap-4 pt-4 items-center">
            <button 
              onClick={() => onPlay(featuredDrama, lastWatched?.episode || 1)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full font-bold text-lg flex items-center gap-2 transform hover:scale-105 transition-all shadow-lg"
            >
              <Play className="w-6 h-6" fill="white" />
              {lastWatched ? `Continue Ep ${lastWatched.episode}` : 'Watch Now'}
            </button>
            
            {featuredDramas.length > 1 && (
              <div className="flex gap-2">
                {featuredDramas.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentIndex ? 'bg-white w-8' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function MyDramaApp() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDrama, setSelectedDrama] = useState(null);
  const [selectedEpisode, setSelectedEpisode] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dramaId = params.get('id');
    if (dramaId) {
      setCurrentRoute(parseInt(dramaId));
    }

    // Mock data with new structure
    const mockData = {
      "sections": [
        {
          "name": "Featured",
          "icon": "featured",
          "dramas": [
            {
              "id": 100,
              "title": "Spark Me Tenderly",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Spark%20Me%20Tenderly/en/2024-10-15%2010:29:36/cover.webp",
			  "landscape": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Spark%20Me%20Tenderly/square/2025-12-05%2014:01:17/cover.webp",
              "description": "Spark Me Tenderly",
              "video_url": "/video/82fcc7af-ba6a-4ab5-97a9-c8c8266f4ec4",
              "total_episodes": 96,
              "rating": "7.52",
              "view": 17109,
              "likes": 581204
            },
			{
              "id": 200,
              "title": "The Alpha's Cursed Luna",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Alpha’s Cursed Luna/en/2025-12-30 11:12:24/cover.webp",
			  "landscape": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The%20Alpha%E2%80%99s%20Cursed%20Luna/square/2025-12-30%2011:12:34/cover.webp",
              "description": "The Alpha’s Cursed Luna",
              "video_url": "/video/84da5139-d0e1-47fe-b704-5957afac6de7",
              "total_episodes": 73,
              "rating": "9.87",
              "view": 90897,
              "likes": 93832
            },
			{
              "id": 101,
              "title": "Sold to the Possessive Mafia Boss",
			  "landscape": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Sold%20to%20the%20Possessive%20Mafia%20Boss/square/2025-11-12%2014:22:33/cover.webp",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Sold%20to%20the%20Possessive%20Mafia%20Boss/en/2024-11-21%2009:58:55/cover.webp",
              "description": "Sold to the Possessive Mafia Boss",
              "video_url": "/video/fc67485f-bb3d-4693-a354-8dd646cefb51",
              "total_episodes": 70,
              "rating": "9.82",
              "view": 0,
              "likes": 327561
            },
			{
              "id": 106,
              "title": "The Shy Beauty and the Billionaire Beast",
			  "landscape": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The+Shy+Beauty+and+the+Billionaire+Beast/clear/2025-04-11+09%3A22%3A39/cover.webp",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/originals/The+Shy+Beauty+and+the+Billionaire+Beast/en/2024-04-25+12:45:15/shy.webp",
              "description": "The Shy Beauty and the Billionaire Beast",
              "video_url": "/video/dd7299ce-4605-4003-855d-35f6c8d675b2",
              "total_episodes": 71,
              "rating": "7.73",
              "view": 14593,
              "likes": 359059
            },
			{
              "id": 107,
              "title": "Love Captive to the Mafia Boss",
			  "landscape": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Love%20Captive%20to%20the%20Mafia%20Boss/square/2025-11-12%2015:13:51/cover.webp",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Love%20Captive%20to%20the%20Mafia%20Boss/en/2024-11-18%2010:30:57/cover.webp",
              "description": "Love Captive to the Mafia Boss",
              "video_url": "/video/11203bf5-0a69-4587-8d08-495d532b557c",
              "total_episodes": 68,
              "rating": "9.74",
              "view": 16176,
              "likes": 496940
            }
          ]
        },
        {
          "name": "VIP Series",
          "icon": "vip",
          "dramas": [
		    {
              "id": 1,
              "title": "Call Me Gianni, Babygirl",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Call%20Me%20Gianni,%20Babygirl/en/2026-04-07%2010:46:03/cover.webp",
              "description": "A young woman working in a strip club is bought by a powerful mafia boss—who is also her best friend's father. Caught between his dangerous obsession and her detective father's investigation, she discovers the price of loyalty and forbidden love.",
              "video_url": "/video/55ea12a0-8241-43b9-823d-bab5e3835e3d",
              "total_episodes": 63,
              "rating": "9.74",
              "view": 16176,
              "likes": 1465844
            },
            {
              "id": 10,
              "title": "Milan In April",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Milan%20In%20April/en/2024-11-20%2016:20:18/cover.webp",
              "description": "After a breakup, April moves to Milan, landing a job at Luxor—but as an assistant to a sleazy boss. Just when she’s ready to quit, a mysterious stranger appears, and his secret could change everything.",
              "video_url": "/video/7342ea55-d2a9-45eb-88ed-1191488f9eb6",
              "total_episodes": 73,
              "rating": "9.68",
              "view": 0,
              "likes": 5932
            },
			{
              "id": 11,
              "title": "Billionaire Disguised as Gigolo",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Billionaire%20Disguised%20as%20Gigolo/en/2024-11-22%2016:45:34/cover.webp",
              "description": "Charlotte’s life falls apart when her husband cheats with her half-sister. A wild night with Gigoloigolo ruins her reputation. Now, caught in secrets and drama, she’ll either lose everything—or win it all.",
              "video_url": "/video/3d5bd0c8-4566-41df-980e-673cfe04226e",
              "total_episodes": 63,
                "rating": "9.80",
                "view": 25432,
                "likes": 69386 
            },
			{
              "id": 12,
              "title": "My Blind Husband is a Billionaire",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My%20Blind%20Husband%20is%20a%20Billionaire/en/2024-10-15%2010:28:45/cover.webp",
              "description": "After a crash leaves her sister in a coma, Lisa sells all she has for surgery funds. A blind billionaire offers to pay if she marries him and becomes his eyes. What's waiting for her now?",
              "video_url": "/video/fddf9d18-2ea8-4f1d-8e64-b26f3e589c73",
              "total_episodes": 96,
              "rating": "9.38",
              "view": 13784,
              "likes": 106237
            },
	    {
              "id": 13,
              "title": "Mate Mine",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Mate%20Mine/en/2024-11-20%2012:37:07/cover.webp",
              "description": "Author Maya retreats to a cabin to heal and find inspiration. She meets Ronan, a wolf-shifter who becomes her muse. As their bond deepens, Maya uncovers the dangers of his world.",
              "video_url": "/video/981ef3c9-e952-4c67-9b18-69344caca605",
              "total_episodes": 66,
              "rating": "8.53",
              "view": 0,
              "likes": 28401
            },
	    {
              "id": 14,
              "title": "A Match Made in Hell",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/A%20Match%20Made%20in%20Hell/en/2024-12-24%2012:36:33/cover.webp",
              "description": "Veronika Aspen is forced to marry cold, ruthless Mason Powell to protect her family’s legacy. But in a world of secrets and power, she must outsmart her dangerous husband—or risk losing everything.",
              "video_url": "/video/1996bc17-44c7-49ab-ad67-df03a5e0d4ee",
              "total_episodes": 63,
              "rating": "9.41",
              "view": 0,
              "likes": 99308
            },
	    {
              "id": 15,
              "title": "Alpha King's Hated Princess",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Alpha%20King's%20Hated%20Princess/en/2024-10-15%2010:25:31/cover.webp",
              "description": "After years of captivity, King Lucien takes back his throne and claims his enemy’s greatest treasure: Princess Danika. He plans to make her suffer for her father’s crimes, but her kindness starts to change him—until Danika...",
              "video_url": "/video/261e563f-2190-41e9-981a-f8bb7609023c",
              "total_episodes": 81,
              "rating": "7.59",
              "view": 15516,
              "likes": 44180
            },
          ]
        },		
        {
          "name": "India Top 10",
          "icon": "crown",
          "dramas": [
            {
              "id": 100,
              "title": "Spark Me Tenderly",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Spark%20Me%20Tenderly/en/2024-10-15%2010:29:36/cover.webp",
			  "landscape": "https://i0.wp.com/fandom.my-drama.com/wp-content/uploads/2025/08/Group-3.png?resize=768%2C348&ssl=1",
              "description": "Spark Me Tenderly series is a true masterpiece that blends romance, ambition, and vulnerability, keeping viewers hooked with its unpredictable twists.",
              "video_url": "/video/82fcc7af-ba6a-4ab5-97a9-c8c8266f4ec4",
              "total_episodes": 96,
              "rating": "7.52",
              "view": 17109,
              "likes": 581204
            },
			{
              "id": 101,
              "title": "Sold to the Possessive Mafia Boss",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Sold%20to%20the%20Possessive%20Mafia%20Boss/en/2024-11-21%2009:58:55/cover.webp",
              "description": "Jane's undercover work leads her to uncover documents and a hidden USB, but her actions spark a deadly game of trust and betrayal.",
              "video_url": "/video/fc67485f-bb3d-4693-a354-8dd646cefb51",
              "total_episodes": 70,
              "rating": "9.82",
              "view": 0,
              "likes": 327561
            },
			{
              "id": 102,
              "title": "A Love Too Risky to Resist",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/A%20Love%20Too%20Risky%20to%20Resist/en/2024-12-11%2008:58:28/cover.webp",
              "description": "Bree and Seth end up in a heartfelt conclusion, making viewers exhale with relief.",
              "video_url": "/video/b8b2a806-1dd3-4d65-b82e-4c050302b7f9",
              "total_episodes": 80,
              "rating": "9.45",
              "view": 0,
              "likes": 231359
            },
			{
              "id": 11,
              "title": "Billionaire Disguised as Gigolo",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Billionaire%20Disguised%20as%20Gigolo/en/2024-11-22%2016:45:34/cover.webp",
              "description": "Charlotte’s life falls apart when her husband cheats with her half-sister. A wild night with Gigoloigolo ruins her reputation. Now, caught in secrets and drama, she’ll either lose everything—or win it all.",
              "video_url": "/video/3d5bd0c8-4566-41df-980e-673cfe04226e",
              "total_episodes": 63,
              "rating": "9.80",
              "view": 25432,
              "likes": 69386
            },
			{
              "id": 103,
              "title": "Act Like You Love Me",
              "portrait": "https://i0.wp.com/fandom.my-drama.com/wp-content/uploads/2025/02/Act-Like-You-Love-Me.jpg?resize=683%2C1024&ssl=1",
              "description": "Walt and Gina’s bond transforms into real love, but just when these two seem to be finally happy, the story ends on a chilling cliffhanger, leaving their future uncertain.",
              "video_url": "/video/6d05a6eb-c93f-49d2-8797-5d47fe94bedd",
              "total_episodes": 65,
              "rating": "9.55",
              "view": 300876,
              "likes": 200755
            },
			{
              "id": 104,
              "title": "Young Elite",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Young%20Elite/en/2024-12-24%2010:43:57/cover.webp",
              "description": "At a school where the rich make the rules, she’s the rebel who refuses to play. He’s the king of the elite. Their hate sets the academy on fire — and so does their desire.",
              "video_url": "/video/c042ab63-66d2-4234-85c3-08fba27dd72e",
              "total_episodes": 67,
              "rating": "9.86",
              "view": 0,
              "likes": 322403
            },
			{
              "id": 105,
              "title": "Bride for Lucifer",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Bride%20for%20Lucifer/en/2025-02-06%2013:43:11/cover.webp",
              "description": "Betrayed by her father and husband, Miriam Byron seeks revenge—striking a deal with the charming millionaire Lucas Craven, who is, in truth, the Devil. Can she trust the Devil, or will his love destroy her?",
              "video_url": "/video/e899f85c-4b09-4e45-955a-4ab44e5a48fc",
              "total_episodes": 66,
              "rating": "9.89",
              "view": 0,
              "likes": 263008
            },
			{
              "id": 106,
              "title": "The Shy Beauty and the Billionaire Beast",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/originals/The+Shy+Beauty+and+the+Billionaire+Beast/en/2024-04-25+12:45:15/shy.webp",
              "description": "Waitress Ana’s life changes when she spills coffee on billionaire Jaxon Terrace. Forced to work as his secretary, she repays her debt—but Jaxon’s growing obsession tests her freedom. Is he a beast or the man she’s been waiting for?",
              "video_url": "/video/dd7299ce-4605-4003-855d-35f6c8d675b2",
              "total_episodes": 71,
              "rating": "7.73",
              "view": 14593,
              "likes": 359059
            },
			{
              "id": 107,
              "title": "Love Captive to the Mafia Boss",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Love%20Captive%20to%20the%20Mafia%20Boss/en/2024-11-18%2010:30:57/cover.webp",
              "description": "When Malissa’s grandma falls ill, a desperate deal binds her to Hayden, a ruthless mafia heir: marriage, a child, and no love allowed. But in his dangerous world, she glimpses the man behind the mask. She never expected that…",
              "video_url": "/video/11203bf5-0a69-4587-8d08-495d532b557c",
              "total_episodes": 68,
              "rating": "9.74",
              "view": 16176,
              "likes": 496940
            },
			{
              "id": 108,
              "title": "The Alpha Mate Who Cried Wolf",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/originals/The+Alpha+Mate+Who+Cried+Wolf/en/2024-04-19+15:43:50/alpha.webp",
              "description": "Astrid escapes her cruel father and works at a diner, where a man claims she’s his “mate” and a rogue. Joining his pack brings trouble, but running away takes her to the pack that ruined her family.",
              "video_url": "/video/42fd7b22-15e9-4c99-a259-d0474a40dc60",
              "total_episodes": 63,
              "rating": "7.95",
              "view": 14737,
              "likes": 278189
            }
          ]
        },
        {
          "name": "New Releases",
          "icon": "sparkles",
          "dramas": [	
		  {
            "id": 229,
            "title": "$$100m To Say I Do",
			"description": "Her wedding to Kieran for a $ 100 million inheritance is shaken when her first love, Luke, returns. With betrayal and secrets around, she must choose between family duty and the man she once loved.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/_dl_100m%20To%20Say%20I%20Do/en/2026-04-07%2007:48:07/cover.webp",
            "video_url": "/video/9f99c4f9-5ea5-4952-be93-f391351c8ef4",
            "total_episodes": 52,
            "rating": "8.02",
            "view": 16647,
            "likes": 16095
          },	
		  {
            "id": 230,
            "title": "Between Ice and Fire",
			"description": "A mafia prince returns from exile, driven by a dark thirst for revenge against his older brother, and his first move is to kidnap the woman promised to him. Trapped in a dangerous game, the honorable heiress fights her growing desire for the captor who is also her enemy.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Between%20Ice%20and%20Fire/en/2026-03-26%2015:00:23/cover.webp",
            "video_url": "/video/566dc90a-d3af-4bdf-8b91-917ad540bd35",
            "total_episodes": 78,
            "rating": "8.02",
            "view": 89871,
            "likes": 68995
          },	
		  {
            "id": 231,
            "title": "Heart of the Yakuza",
			"description": "A waitress goes undercover in a Yakuza nightclub to clear her name, but a dangerous attraction to the powerful manager threatens to expose her mission. Caught between her duty to the police and her feelings for the man who murdered her father, Sofia must choose her loyalties before it's too late.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Heart%20of%20the%20Yakuza/en/2026-03-25%2014:09:00/cover.webp",
            "video_url": "/video/c44c59c3-9dea-4828-82ac-ec58d5170210",
            "total_episodes": 67,
            "rating": "8.02",
            "view": 20231,
            "likes": 19285
          },	
		  {
            "id": 232,
            "title": "Heart of the Yakuza - 2",
			"description": "Sofia and Jun must hide their love to stay safe from the dangerous criminal group. After helping the police arrest Jun's violent brother, Jun leaves his life of crime so he and Sofia can start a new life together. Can they truly escape their dark past, or will the shadows of the Yakuza follow them forever?",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Heart%20of%20the%20Yakuza-2/en/2026-03-31%2016:41:28/cover.webp",
            "video_url": "/video/f2cdde52-702d-4cfe-9bec-35635ba77f58",
            "total_episodes": 69,
            "rating": "8.02",
            "view": 16433,
            "likes": 15900
          },	
		  {
            "id": 233,
            "title": "Claimed by the Crown",
			"description": "April is a waitress who agrees to a contract marriage with Prince Christopher to escape her abusive stepfather. She moves to a grand palace and must face dangerous enemies while learning to live as a princess.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Claimed%20by%20the%20Crown/en/2026-03-12%2014:00:47/cover.webp",
            "video_url": "/video/432a3003-9b2c-496e-9205-8ada4d497ff5",
            "total_episodes": 76,
            "rating": "8.02",
            "view": 88711,
            "likes": 86781
          },	
		  {
            "id": 234,
            "title": "Falling for the Wrong Bride",
			"description": "To save her sick mother, waitress Sky agrees to a 3-month fake marriage with cold billionaire Grayson, who needs to secure his inheritance. Trapped in a hostile family, she uncovers a dangerous plot of manipulation and secrets orchestrated by his mother and rival half-brother.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Falling%20for%20the%20Wrong%20Bride/en/2026-03-27%2014:29:17/cover.webp",
            "video_url": "/video/be05cd83-80c7-4d64-94be-0b2a306d2422",
            "total_episodes": 61,
            "rating": "8.02",
            "view": 44772,
            "likes": 43988
          },	
		  {
            "id": 235,
            "title": "The Chosen Luna: Alpha’s Unwanted Daughter",
			"description": "Rejected by her own family, Luna-to-be Ayla flees into enemy territory where the ruthless Alpha, Draven, is her fated mate. Caught in his pack's dark world, she must fight a conspiracy to survive without the wolf she can't shift into.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The%20Chosen%20Luna_cl_%20Alpha%E2%80%99s%20Unwanted%20Daughter/en/2026-03-26%2010:16:18/cover.webp",
            "video_url": "/video/d375da9d-875e-4658-8261-aa2ccd64b520",
            "total_episodes": 59,
            "rating": "8.02",
            "view": 594489,
            "likes": 89101
          },	
		  {
            "id": 236,
            "title": "Wrestling With Love",
			"description": "Forced onto the wrestling team together, a bully and his target discover a surprising spark that turns their rivalry into a passionate romance. They must battle social pressure and dark secrets to protect the love they never thought they would find. HolyTagsYo: Gay Romance, BL (Boys Love)",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Wrestling%20with%20Love/en/2026-03-05%2012:10:55/cover.webp",
            "video_url": "/video/be8ea5b8-84bd-47bd-bd18-36ca3cdb8d6f",
            "total_episodes": 69,
            "rating": "8.02",
            "view": 1015561,
            "likes": 68597
          },	
		  {
            "id": 237,
            "title": "A Queen for the Mafia Kings",
			"description": "A lot at an illegal auction. She had never thought that she would be stuck in such a dangerous situation. Now Jasmine is surrounded by three powerful brothers and forced into a game of power, obsession, and buried memories, with very unexpected bets.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/A%20Queen%20for%20the%20Mafia%20Kings/en/2026-03-12%2015:17:27/cover.webp",
            "video_url": "/video/f6f1c462-87f3-4d92-8e26-4f22e4c0ece0",
            "total_episodes": 90,
            "rating": "8.02",
            "view": 1003769,
            "likes": 91814
          },	
		  {
            "id": 238,
            "title": "All In: Double or Die",
			"description": "A woman is forced to cheat in a high-stakes, secret poker tournament to save her fiancé's life. As she navigates a web of lies involving terrorists and government spies, she must decide who to trust before the final cards are shown.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/All%20In_cl_%20Double%20or%20Die/en/2026-03-13%2010:26:19/cover.webp",
            "video_url": "/video/21c03f21-bc1c-4ceb-946f-b4aed6a643d8",
            "total_episodes": 62,
            "rating": "8.02",
            "view": 32230,
            "likes": 24559
          },	
		  {
            "id": 239,
            "title": "The Wolf's Claim",
			"description": "Investigative journalist Lyra is bitten by the Alpha werewolf Pierce Lenegan while trying to expose a corrupt governor. Now Lyra struggles with her transformation and growing attraction to her fated mate. This new, perilous existence is made even more complicated by Pierce's rogue brother who plots with the governor to take him down.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The%20Wolf_sq_s%20Claim/en/2026-02-12%2011:19:26/cover.webp",
            "video_url": "/video/1878ad55-9146-457e-935b-26fdb547658d",
            "total_episodes": 62,
            "rating": "8.02",
            "view": 47320,
            "likes": 44159
          },	
		  {
            "id": 240,
            "title": "Pretend You Love Me",
			"description": "Zoe is a quiet outsider, humiliated by everyone and broken in public. When her life falls apart, Chase appears — a dangerous biker with a dark past. He offers a fake relationship for protection and revenge.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Pretend%20You%20Love%20Me/en/2026-02-12%2009:44:15/cover.webp",
            "video_url": "/video/0b74e253-b652-4349-bc08-ae235a19407c",
            "total_episodes": 78,
            "rating": "8.02",
            "view": 43230,
            "likes": 35811
          },	
		  {
            "id": 241,
            "title": "Maca the Snacker",
			"description": "Scholarship student Maca's new life turns into a nightmare when she clashes with college king Emiliano, the rich bully who makes her his first target. Their fiery rivalry becomes dangerously complicated when they are forced to be partners in a dramatic competition, all while a dark, high-stakes bet threatens to expose their explosive, secret attraction.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Maca%20the%20Snacker/en/2026-02-10%2015:18:10/cover.webp",
            "video_url": "/video/9d95c2f1-3f05-4d91-a672-dcee4de1092e",
            "total_episodes": 85,
            "rating": "8.02",
            "view": 744654,
            "likes": 32819
          },	
		  {
            "id": 242,
            "title": "Wild Silence",
			"description": "A woman trying to die is saved by a wild, quiet man who keeps her in his remote forest cabin to teach her hard survival rules. As they face the wild and violent hunters, their intense bond is tested by his dark secrets and the choice to accept death or truly start a new life.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Wild%20silence/en/2026-03-06%2009:53:33/cover.webp",
            "video_url": "/video/a36a7fe3-0e89-45ff-a409-f75093c5144f",
            "total_episodes": 56,
            "rating": "8.02",
            "view": 62718,
            "likes": 52714
          },
		  {
            "id": 225,
            "title": "Bride for Quarterback",
			"description": "A humble janitor, desperate to save her sick son, agrees to a fake engagement with the NFL's most arrogant, scandal-ridden star quarterback to rebuild his image. As their public charade is tested by jealous rivals and dark secrets, their undeniable passion ignites, turning a simple business deal into a dangerous game.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Bride%20for%20Quarterback/en/2026-01-27%2010:19:50/cover.webp",
            "video_url": "/video/070728a2-7586-44c0-baff-f79eefa05a0a",
            "total_episodes": 66,
            "rating": "8.02",
            "view": 29871,
            "likes": 59923
          },
		  {
            "id": 226,
            "title": "One Face Two Loves",
			"description": "A newlywed with amnesia and a new face is trapped by a doctor who convinces her she is his dead wife. As her real husband desperately searches for her, she must uncover which of the two men is the true monster before she loses her life again.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/One%20Face%20Two%20Loves/en/2026-01-21%2012:55:20/cover.webp",
            "video_url": "/video/4e1a3ccd-f9a8-41dc-835c-bf6aa009211c",
            "total_episodes": 64,
            "rating": "8.02",
            "view": 29871,
            "likes": 15746
          },
		  {
            "id": 227,
            "title": "Untouched Scent",
			"description": "A desperate Skyler enters the dark, luxurious world of a billionaire perfume CEO whose strange obsession is her \"pure\" scent. She signed a contract for her mother's life, but now she realizes the fine print means she belongs to him, body and soul.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Untouched%20Scent/en/2025-12-24%2016:45:56/cover.webp",
            "video_url": "/video/f75a5396-db23-45e5-adc1-e7dc5a84ecb3",
            "total_episodes": 64,
            "rating": "8.02",
            "view": 29871,
            "likes": 21503
          },
		  {
            "id": 228,
            "title": "Married Against the Will",
			"description": "Trapped in a sudden marriage to the cold and powerful Bobby Plainview, Ava's life becomes a high-stakes deal to save her family. She quickly learns that beneath his cruel control lies a dangerous desire, and the line between her duty and her growing passion for her captor is razor-thin.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Married%20Against%20The%20Will/en/2026-01-06%2013:44:56/cover.webp",
            "video_url": "/video/c50e5fba-3920-4ad3-a544-345848af603b",
            "total_episodes": 71,
            "rating": "8.02",
            "view": 29871,
            "likes": 38143
          },	  
		  {
            "id": 222,
            "title": "Mr. Denver",
			"description": "Headstrong Dakota Lennox is violently drawn into the world of cold diamond magnate and mafia boss Kyle Denver when he buys her life at a mysterious auction. Trapped between her family duty and the danger of his powerful criminal empire, Dakota's only weapon is the courage to melt the icy heart of the man who now owns her.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Mr. Denver/en/2026-01-12 16:46:09/cover.webp",
            "video_url": "/video/4a3c19e3-0a3f-415f-88d0-fe623e843313",
            "total_episodes": 71,
            "rating": "9.24",
            "view": 89908,
            "likes": 51631
          },
		  {
            "id": 223,
            "title": "I'm Not Allowed to Love You",
			"description": "Pregnant and broke after her husband’s sudden death, Julia is taken in by his powerful family. But when his suspicious brother becomes her protector, grief turns into a dangerous, forbidden attraction.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/I'm Not Allowed to Love You/en/2026-01-15 11:15:40/cover.webp",
            "video_url": "/video/3f49be3d-0d29-4e6d-994e-b1c2eb2dd02b",
            "total_episodes": 62,
            "rating": "8.02",
            "view": 29871,
            "likes": 8787
          },
		  {
            "id": 224,
            "title": "The Cursed Bride of the Cold Prince",
			"description": "Locked away as a cursed girl, Cecilia risks everything to force a ruthless prince into a contract marriage. What begins as blackmail slowly turns into a dangerous love neither of them expected.",
			"portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Cursed Bride of the Cold Prince/en/2026-01-15 11:21:04/cover.webp",
            "video_url": "/video/b574a2ee-932b-4dcb-afdc-7a33ff7eec75",
            "total_episodes": 55,
            "rating": "7.89",
            "view": 18989,
            "likes": 7679
          },
		  {
            "id": 210,
            "title": "Escape from Mile Marker Zero",
			"description": "After escaping her abusive boyfriend, Portia is saved by a stranger who takes her to a remote lake house. But when she discovers his dark past, she realizes she may have traded one danger for another.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Escape from Mile Market Zero/en/2025-10-21 16:01:13/cover.webp",
            "video_url": "/video/f14982e6-83b3-4006-813f-32ecfdd4d354",
            "total_episodes": 76,
            "rating": "9.09",
            "view": 0,
            "likes": 18313
          },
          {
            "id": 211,
            "title": "Falling for Tarzan",
			"description": "A spoiled city girl gets dumped in the wild by her rich boyfriend—only to be rescued by a mysterious forest man who’s never known modern life.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Falling for Tarzan/en/2025-10-27 14:19:55/cover.webp",
            "video_url": "/video/acf17517-b0e8-40e8-9e35-6ee86fda6f2a",
            "total_episodes": 38,
            "rating": "8.65",
            "view": 15436,
            "likes": 16690
          },
          {
            "id": 212,
            "title": "I Kiss My Ex's Son",
			"description": "After a cruel betrayal by her rich lover, Meg finds comfort in the arms of a charming young man — only to discover he’s her ex’s son. What begins as revenge soon turns into forbidden love that could destroy them all.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/I Kiss My Ex's Son/en/2025-10-22 11:38:47/cover.webp",
            "video_url": "/video/76245fcb-e2d1-4d6f-bc85-8817cd9aadac",
            "total_episodes": 59,
            "rating": "9.32",
            "view": 21321,
            "likes": 21195
          },
          {
            "id": 213,
            "title": "Night with My Vampire Mate",
			"description": "In a world of vampire-human enmity, a Vampire King’s forbidden love with a human ignites a clash with his clan. Will he choose blood or protect his mate?",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Night with My Vampire Mate/en/2024-10-15 10:29:10/cover.webp",
            "video_url": "/video/db8ba957-d57e-493f-918c-c45208fb0da6",
            "total_episodes": 60,
            "rating": "8.30",
            "view": 17714,
            "likes": 8604
          },
          {
            "id": 214,
            "title": "Queen of Hearts",
			"description": "Andrea, a poor but strong student, enters an elite academy where status is decided by a deck of cards. The cruel King of Hearts, Nathaniel Sinclair, rules the game. By accident, Andrea becomes the new Queen of Hearts. Now she is trapped in a world of dangerous secrets, and a growing attraction to the boy who could destroy her.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Queen of Hearts/en/2025-10-09 09:50:40/cover.webp",
            "video_url": "/video/7c9fa9b3-f68b-4042-a72c-3b41ecac18d2",
            "total_episodes": 95,
            "rating": "9.86",
            "view": 34532,
            "likes": 538283
          },
          {
            "id": 215,
            "title": "Reality Show. Love or Dare. Season 1",
			"description": "24 y.o blogger Anastasia and 46 y.o financial director Ricardo try to prove that love can survive a major age gap. But jealousy and money push them to the edge. Can a carefree dreamer and a strict perfectionist find balance—or will their differences destroy their relationship?",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Reality Show. Love or Dare. Season 1/en/2025-11-20 14:19:58/cover.webp",
            "video_url": "/video/f2199e20-f462-4d54-a2d6-b9d391b4b28e",
            "total_episodes": 80,
            "rating": "9.04",
            "view": 28976,
            "likes": 27867
          },
          {
            "id": 216,
            "title": "She Came Out of a Coma After 7 Years",
			"description": "When a pregnant woman’s perfect life shatters after a tragic accident, she wakes up from a coma to find her fiancé now with her jealous sister. Heartbroken and betrayed, she begins to rebuild her world—unaware that the man who’s always loved her in secret is right by her side, ready to help her rise again.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/She Came Out of a Coma After 7 Years/en/2025-10-17 05:58:34/cover.webp",
            "video_url": "/video/8bc85b7b-223b-4b28-8e29-50c05acd53c4",
            "total_episodes": 71,
            "rating": "8.45",
            "view": 16754,
            "likes": 14171
          },
          {
            "id": 217,
            "title": "Snow White for Basketball Team",
			"description": "She escapes an abusive fiancé and crashes into the world of five irresistible basketball stars — each ready to fight for her heart. But when their strict, cold-hearted coach becomes the one man she can’t stop thinking about, her dangerous ex isn’t the only problem anymore.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Snow White for Basketball Team/en/2025-11-28 16:39:20/cover.webp",
            "video_url": "/video/8724c1f4-a548-4acb-a329-95956777190f",
            "total_episodes": 68,
            "rating": "8.98",
            "view": 18765,
            "likes": 16471
          },
          {
            "id": 218,
            "title": "Teach Me, Touch Me",
			"description": "She was top of her class — until her new professor became her biggest lesson. Now caught between lust, lies, and secrets, Brianna must decide: follow her desire or survive it.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Teach Me, Touch Me/en/2025-05-14 07:14:20/cover.webp",
            "video_url": "/video/ac700c57-4dda-43d4-a0e9-d27b5cff1c04",
            "total_episodes": 71,
            "rating": "8.99",
            "view": 48976,
            "likes": 25244
          },
          {
            "id": 219,
            "title": "The Deadly Prince",
			"description": "A deadly reality show traps young women in a locked mansion, where the prince must choose only one bride. To win love means survival — but to lose means death.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Deadly Prince/en/2025-09-30 13:59:55/cover.webp",
            "video_url": "/video/fbdb1610-bde9-462d-bee9-0e30185c161f",
            "total_episodes": 87,
            "rating": "9.87",
            "view": 25432,
            "likes": 17238
          },
          {
            "id": 220,
            "title": "The Diamond Rose",
			"description": "A shy ballet teacher enters a seductive strip club world to save her father — but love, danger, and a powerful crime boss soon turn her life upside down. How far will she go to protect the ones she loves?",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Diamond Rose/en/2025-06-13 11:49:52/cover.webp",
            "video_url": "/video/3117eea3-96eb-46d4-be62-f3fd7ffbf5d0",
            "total_episodes": 71,
            "rating": "9.12",
            "view": 45654,
            "likes": 334320
          },
		  {
            "id": 221,
            "title": "The Bodyguard I Can’t Escape",
			"description": "He’s nearly twice her age — all discipline, control, and buried wounds — while she’s a storm still learning where to land. But when danger closes in and the truth about her past cracks open, the man she swore to hate becomes the only one who sees the fire she’s been hiding.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Bodyguard I Can’t Escape/en/2025-12-29 08:38:20/cover.webp",
            "video_url": "/video/0704afe8-0536-4cbc-bf35-2aff5f255d8c",
            "total_episodes": 77,
            "rating": "9.76",
            "view": 67564,
            "likes": 56059
          },
            {
              "id": 200,
              "title": "The Alpha's Cursed Luna",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Alpha’s Cursed Luna/en/2025-12-30 11:12:24/cover.webp",
			  "landscape": "https://i0.wp.com/fandom.my-drama.com/wp-content/uploads/2025/12/image-128.png?resize=768%2C393&ssl=1",
              "description": "The Alpha’s Cursed Luna",
              "video_url": "/video/84da5139-d0e1-47fe-b704-5957afac6de7",
              "total_episodes": 73,
              "rating": "9.87",
              "view": 90897,
              "likes": 93832
            },
			{
              "id": 201,
              "title": "Love Score",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Love Score/en/2025-12-29 09:38:09/cover.webp",
              "description": "Love Score",
              "video_url": "/video/376d38b4-1020-4e5b-ac82-994ebf3472e4",
              "total_episodes": 75,
              "rating": "9.34",
              "view": 76897,
              "likes": 54097
            },
			{
              "id": 202,
              "title": "Swapped Bodies With My Secret Crush",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Swapped Bodies With My Secret Crush/en/2025-12-16 19:13:43/cover.webp",
              "description": "Swapped Bodies With My Secret Crush",
              "video_url": "/video/467c3999-1019-4cd4-8159-7f303599b349",
              "total_episodes": 76,
              "rating": "9.34",
              "view": 76897,
              "likes":54097 
            },
			{
              "id": 203,
              "title": "Thin Ice",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Thin Ice/en/2025-12-23 12:11:23/cover.webp",
              "description": "Thin Ice",
              "video_url": "/video/2b643449-9856-47f9-b6ec-1d66c516da39",
              "total_episodes": 68,
              "rating": "9.25",
              "view": 49876,
              "likes": 91631
            },
			{
              "id": 204,
              "title": "Obsessed With You",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Obsessed With You/en/2025-12-10 13:29:22/cover.webp",
              "description": "Obsessed With You",
              "video_url": "/video/6e9605b3-2303-417e-bd11-01c44fd4b7e9",
              "total_episodes": 55,
              "rating": "9.32",
              "view": 65453,
              "likes": 46108
            },
			{
              "id": 205,
              "title": "Reality Show. Love or Dare. Season 2",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Reality Show. Love or Dare. Season 2/en/2025-12-29 09:04:46/cover.webp",
              "description": "Reality Show. Love or Dare. Season 2",
              "video_url": "/video/b57f579b-a2ea-4307-a77c-ec01c1888d5c",
              "total_episodes": 67,
              "rating": "9.04",
              "view": 23432,
              "likes": 23116
            },
			{
              "id": 206,
              "title": "No, Your Majesty",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/No, Your Majesty/en/2025-12-12 12:11:36/cover.webp",
              "description": "No, Your Majesty",
              "video_url": "/video/d954b5e6-0941-428c-bb57-a5dcd2cc9a1e",
              "total_episodes": 81,
              "rating": "9.45",
              "view": 87675,
              "likes": 332502
            },
			{
              "id": 207,
              "title": "Bound By Obsession",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Bound By Obsession/en/2025-12-12 11:33:11/cover.webp",
              "description": "Bound By Obsession",
              "video_url": "/video/a51a22fb-1c8f-45fb-b39b-e7ba90ca3806",
              "total_episodes": 74,
              "rating": "9.23",
              "view": 38765,
              "likes": 39612
            },
			{
              "id": 208,
              "title": "Her Heart Held Hostage",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Her Heart Held Hostage/en/2025-11-17 10:09:25/cover.webp",
              "description": "Her Heart Held Hostage",
              "video_url": "/video/081898d1-7429-4095-8210-e58ab769a42c",
              "total_episodes": 52,
              "rating": "9.06",
              "view": 87678,
              "likes": 54717
            },
			{
              "id": 209,
              "title": "Secret Affair with My Billionaire Boss",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Secret Affair with My Billionaire Boss/en/2025-12-17 17:28:02/cover.webp",
              "description": "Secret Affair with My Billionaire Boss",
              "video_url": "/video/3d98adf5-7ac8-463a-a46b-5be7e9befe96",
              "total_episodes": 48,
              "rating": "7.22",
              "view": 7656,
              "likes": 9778
            }
          ]
        },
        {
          "name": "Most Trending",
          "icon": "trending",
          "dramas": [
            {
              "id": 300,
              "title": "Under the Royal Rule",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Under the Royal Rule/en/2025-02-14 15:14:18/cover.webp",
              "description": "Under the Royal Rule",
              "video_url": "/video/1dc1cf09-1318-4cc9-8e5d-68cd7238fa10",
              "total_episodes": 61,
              "rating": "9.45",
              "view": 0,
              "likes": 208401
            },
			{
              "id": 301,
              "title": "My Golden Cage",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My Golden Cage/en/2025-03-20 08:27:05/cover.webp",
              "description": "My Golden Cage",
              "video_url": "/video/3e0d186e-b67b-4ca6-bc06-8a9b3508cd63",
              "total_episodes": 62,
              "rating": "9.45",
              "view": 89076,
              "likes": 85386
            },
			{
              "id": 302,
              "title": "Bound By Blood",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Bound By Blood/en/2025-01-20 07:51:50/cover.webp",
              "description": "Bound By Blood",
              "video_url": "/video/5e8b41d6-eebb-4b5c-bbcb-e99a6af1e1c1",
              "total_episodes": 66,
              "rating": "9.04",
              "view": 0,
              "likes": 274324
            },
			{
              "id": 303,
              "title": "His Fake Bride Was a Homeless Girl",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/His Fake Bride Was a Homeless Girl/en/2025-12-17 17:02:17/cover.webp",
              "description": "His Fake Bride Was a Homeless Girl",
              "video_url": "/video/e386f187-82f4-4c3f-8a3d-9602d2709d42",
              "total_episodes": 51,
              "rating": "9.04",
              "view": 0,
              "likes": 274324
            },
			{
              "id": 304,
              "title": "My New Boss Is My Dead Ex-Boyfriend",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My New Boss Is My Dead Ex-Boyfriend/en/2025-12-17 17:10:42/cover.webp",
              "description": "My New Boss Is My Dead Ex-Boyfriend",
              "video_url": "/video/95cccbad-9f13-44ae-8723-b99fc2fb4df8",
              "total_episodes": 60,
              "rating": "7.90",
              "view": 21342,
              "likes": 8519
            },
			{
              "id": 305,
              "title": "The President's Secret Daughter",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The President's Secret Daughter/en/2025-12-05 17:45:17/cover.webp",
              "description": "The President's Secret Daughter",
              "video_url": "/video/c2115bb6-dbca-4a28-9b32-4864448cb434",
              "total_episodes": 76,
              "rating": "9.43",
              "view": 89765,
              "likes": 59684
            },
			{
              "id": 306,
              "title": "Love, Lies and a Dead Tycoon",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Love, Lies and a Dead Tycoon/en/2025-12-02 11:39:47/cover.webp",
              "description": "Love, Lies and a Dead Tycoon",
              "video_url": "/video/7d0d6a98-e626-4abc-a56f-e794f5b1935d",
              "total_episodes": 74,
              "rating": "9.21",
              "view": 43238,
              "likes": 24787
            },
			{
              "id": 307,
              "title": "I'm Her Most Dangerous Obsession",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/I’m Her Most Dangerous Obsession/en/2025-11-18 13:40:03/cover.webp",
              "description": "I'm Her Most Dangerous Obsession",
              "video_url": "/video/8946790e-8f6f-408e-a440-7896b5615b46",
              "total_episodes": 83,
              "rating": "9.87",
              "view": 56765,
              "likes": 600194
            },
			{
              "id": 308,
              "title": "Hiding in His Shower",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Hiding in His Shower/en/2025-12-03 09:34:31/cover.webp",
              "description": "Hiding in His Shower",
              "video_url": "/video/22502f60-4073-44b8-a81c-137459f2aa4f",
              "total_episodes": 62,
              "rating": "9.09",
              "view": 43342,
              "likes": 19856
            },
			{
              "id": 309,
              "title": "Make Me Yours",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Make Me Yours/en/2025-11-12 16:04:07/cover.webp",
              "description": "Make Me Yours",
              "video_url": "/video/a0deec29-8abc-4b1c-be77-4d4107019877",
              "total_episodes": 88,
              "rating": "9.87",
              "view": 58907,
              "likes": 35162
            }
          ]
        },
		{
          "name": "Best option",
          "icon": "best",
          "dramas": [
            {
              "id": 400,
              "title": "Too Young to Want her Professor",
              "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Too%20Young%20to%20Want%20her%20Professor/en/2025-05-28%2007:42:20/cover.webp",
              "description": "After her mother’s death, college freshman Mia tries to continue her legacy as a romance writer—but things spiral when a humiliating classroom moment leads to a unexpected power dynamic with her new professor.",
              "video_url": "/video/1328c2e1-b833-4580-b6bc-f949dea73b8a",
              "total_episodes": 72,
              "rating": "9.54",
              "view": 13453,
              "likes": 533657
            },
			{
              "id": 401,
              "title": "The Mafia Beast Won Me in a Gamble",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The%20Mafia%20Beast%20Won%20Me%20in%20a%20Gamble/en/2025-07-07%2015:29:01/cover.webp",
                "description": "To pay her father's debt, Mary is forced to marry a scarred mafia boss. In a world of danger and desire, she learns to gamble — with her heart.",
                "video_url": "/video/6e8235f0-2e74-4ff9-963c-db071129f78b",
              "total_episodes": 67,
              "rating": "9.21",
              "view": 407651,
              "likes": 522413
            },
			{
              "id": 402,
              "title": "Take My Breath Away",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Take%20My%20Breath%20Away/en/2025-06-11%2009:34:14/cover.webp",
                "description": "When Annabelle marries Prince Henry to save her sister, she must wear a collar that chokes her when she disobeys. Royal family members are dying, and Henry's other wives are missing. Annabelle must find the killer fast before she becomes the next victim.",
                "video_url": "/video/e224a0db-5c58-4341-90da-d85dfab87660",
              "total_episodes": 71,
              "rating": "9.21",
              "view": 56789,
              "likes": 513383
            },
			{
              "id": 403,
              "title": "What Was I Made For",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/What%20Was%20I%20Made%20For%20/en/2025-07-09%2010:30:06/cover.webp",
                "description": "On her sister's wedding day, Eva is forced to take her place and marry a cold, powerful billionaire. Now trapped in a fake marriage she must fight to protect her family - and her heart. But nothing can prepare her for the price he wants in return.",
                "video_url": "/video/bc5cf71f-5f65-4a61-b44c-87796fdb030c",
              "total_episodes": 81,
              "rating": "9.87",
              "view": 48907,
              "likes": 520398
            },
			{
              "id": 404,
              "title": "Designed for Desire",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Designed%20for%20Desire/en/2025-07-10%2008:25:06/cover.webp",
                "description": "When struggling artist Cecilia Brooks takes a job at a powerful architect’s studio, she quickly realizes it’s not just about design—it’s about control. Now she’s trapped in a mansion with a billionaire who never plays fair.",
                "video_url": "/video/337de66d-70e6-483d-a199-2c476942e18c",
              "total_episodes": 68,
              "rating": "8.76",
              "view": 38907,
              "likes": 27393
            },
			{
              "id": 405,
              "title": "The Class of Lust and Lies",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The%20Class%20of%20Lust%20and%20Lies/en/2025-06-06%2009:34:26/cover.webp",
                "description": "At Kingston University, poor girl Nora is pulled into a dark world of power and secrets. Her life depends on outsmarting Elliot, the leader of the powerful A.K.B. fraternity. Will she fight back or lose everything?",
                "video_url": "/video/20764934-17db-420b-aafa-6814301da6d7",
                "total_episodes": 57,
              "rating": "9.12",
              "view": 20564,
              "likes": 23213
            },
			{
              "id": 406,
              "title": "I Became My CEO's Darkest Secret",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/I%20Became%20My%20CEO's%20Darkest%20Secret/en/2025-05-28%2013:14:20/cover.webp",
                "description": "Iris thought she was just a secretary—until her CEO claimed her as his. Caught in a game of power and desire, she must decide: resist him, or lose herself. But what if he’s hiding more than just feelings?",
                "video_url": "/video/87ef1acd-f5f4-4ab0-933d-9a9c12bc87b9",
                "total_episodes": 64,
              "rating": "9.07",
              "view": 18976,
              "likes": 530072
            },
			{
              "id": 407,
              "title": "Brace Face Betty",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Brace%20Face%20Betty/en/2025-05-20%2012:37:32/cover.webp",
                "description": "Shy, braces-wearing Betty becomes the target of her cruel stepsister, but when the school's hottest bad boy, Marcus, takes notice, everything changes. Is he playing a game, or is there something real between them?",
                "video_url": "/video/a451604b-2f56-4a25-a863-c8ab82b582fc",
                "total_episodes": 52,
              "rating": "9.58",
              "view": 45678,
              "likes": 335801
            },
			{
              "id": 408,
              "title": "First Tender Spark",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/First%20Tender%20Spark/en/2025-05-07%2012:03:36/cover.webp",
                "description": "Helena lands her dream job — but to get it, she must work for Eric, a spoiled heir with dark secrets. Caught between ambition, obsession, and danger, can she survive the world of fashion?",
                "video_url": "/video/4599d5a6-a070-44e0-afc7-2153a291c064",
                "total_episodes": 72,
              "rating": "9.02",
              "view": 0,
              "likes": 254639
            },
			{
              "id": 409,
              "title": "Taboo Match",
                "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Taboo%20Match/en/2025-03-28%2007:52:07/cover.webp",
                "description": "When sweet Anna meets her dangerous new stepbrother, sparks fly. But secrets, betrayal, and forbidden love put them both in danger.",
                "video_url": "/video/76f5b305-2679-48c7-875c-82e28e02f940",
                "total_episodes": 58,
              "rating": "9.34",
              "view": 0,
              "likes": 273214
            },
          {
              "id": 410,
            "title": "Engaged to the Enemy",
			"description": "Annie’s life changes when she learns a secret—her father is a mafia boss, and now she must marry his enemy, Daniel. He is cold, powerful, and dangerous. She is just part of a deal. As danger rises and secrets come out, she must decide—fight for her freedom or fall for the man she was never supposed to love.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Engaged to the Enemy/en/2025-02-28 15:46:01/cover.webp",
            "video_url": "/video/70ea5eae-acd9-4372-b735-edf30ad38bbe",
            "total_episodes": 59,
            "rating": "8.98",
            "view": 0,
            "likes": 520747
          },
          {
              "id": 411,
            "title": "In Bed with the Enemy",
			"description": "When mob boss Vincent rescues waitress Sameen from trouble, a fake kiss sparks a passionate night. But her secret life could turn their act into real danger.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/In Bed with the Enemy/en/2024-10-15 12:19:05/cover.webp",
            "video_url": "/video/d54bb049-804d-4dd6-9164-07fb0ccf9f87",
            "total_episodes": 60,
            "rating": "9.18",
            "view": 17805,
            "likes": 9500
          },
          {
              "id": 412,
            "title": "Revenge of a Mafia Heiress",
			    "description": "Kelsey, a mafia bodyguard, wants to leave the life—and her possessive boss, Jonathan. Torn between her feelings and a hidden past, she must choose: freedom or the truth that could tie her to him forever.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Revenge of a Mafia Heiress/en/2024-10-15 12:19:24/cover.webp",
            "video_url": "/video/2ef0a8c2-becc-499c-a6e5-7e76ef7c420a",
            "total_episodes": 60,
            "rating": "8.21",
            "view": 17502,
            "likes": 9536
          },
          {
              "id": 413,
            "title": "The Mafia's Broken Doll",
		      "description": "She made a deal with a mafia boss to save her mother. Now, she’s trapped in his world—his rules, his touch, his obsession.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Mafia’s Broken Doll/en/2025-04-30 14:55:48/cover.webp",
            "video_url": "/video/072fba6e-84bd-4600-815d-eeb15271c338",
            "total_episodes": 62,
            "rating": "9.65",
            "view": 0,
            "likes": 22771
          },
          {
              "id": 414,
            "title": "Twisted Fates",
			    "description": "Finch, a devoted husband, fights to build a happy life for his wife Mira and their son. But when love and desire clash, his choices threaten to shatter everything he holds dear.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Twisted Fates/en/2024-06-12 21:44:51/cover.webp",
            "video_url": "/video/2b50ae51-7f3c-4c52-8399-ce6bbef6ca96",
            "total_episodes": 49,
            "rating": "8.83",
            "view": 15724,
            "likes": 3216
          }			
          ]
        },
      {
        "name": "Billionaire",
        "icon": "billionaire",
        "dramas": [		  
		  {
			"id": 700,
            "title": "After One Night",
			"description": "Emily’s life changes after a one-night stand with her boss, Richard, leaves her unexpectedly pregnant.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/One Night Stand/en/2025-03-04 12:24:01/cover.webp",
            "video_url": "/video/4d7caaae-142a-4017-b617-6a7ec2411225",
            "total_episodes": 50,
            "rating": "9.60",
            "view": 14155,
            "likes": 7559
          },
          {
			"id": 701,
            "title": "Billionaire Blackmail",
			      "description": "Trapped in her boss’s luxurious estate, Samantha uncovers a deadly conspiracy that ties her family’s fate to his empire. Love was never part of the plan — until it’s too late to walk away.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Billionaire Blackmail/en/2025-10-29 11:04:21/cover.webp",
            "video_url": "/video/dcad7a7b-e9c5-460a-bf80-d86b2677a7c6",
            "total_episodes": 68,
            "rating": "9.32",
            "view": 18976,
            "likes": 21093
          },
          {
			"id": 702,
            "title": "Billionaire Blackmeil",
			      "description": "Trapped in her boss’s luxurious estate, Samantha uncovers a deadly conspiracy that ties her family’s fate to his empire. Love was never part of the plan — until it’s too late to walk away.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Billionaire Blackmail/en/2025-10-29 11:04:21/cover.webp",
            "video_url": "/video/i70w5Jey79PYEXI9RccE",
            "total_episodes": 68,
            "rating": "9.32",
            "view": 18976,
            "likes": 20885
          },
          {
			"id": 703,
			      "description": "She lied to save her father. But now she’s pregnant by a man who thinks she’s someone else.",
            "title": "I Seduced a Billionaire for My Stepsister",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/I Seduced a Billionaire for My Stepsister/en/2025-07-22 11:37:41/cover.webp",
            "video_url": "/video/7bd5644e-9c15-4d8d-ad3f-bedf9ec11511",
            "total_episodes": 67,
            "rating": "8.29",
            "view": 18765,
            "likes": 8889
          },
          {
			"id": 704,
            "title": "I'm Obsessed With My Boss Part I",
			    "description": "Lucilia’s boring life takes a turn when she falls for her boss, Ricardo. Caught by her jealous boyfriend, Jeremy, secrets explode as her affair with Ricardo begins.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/I'm Obsessed With My Boss Part I/en/2024-10-15 11:48:18/cover.webp",
            "video_url": "/video/09123b6d-1db7-467f-a4a4-056085f1779a",
            "total_episodes": 50,
            "rating": "8.73",
            "view": 14215,
            "likes": 3311
          },
          {
			"id": 705,
            "title": "Chained by Her Love",
			      "description": "Leslie needs money to save her mother. She gets a job as an assistant to a powerful lingerie CEO — a woman with strict rules and secret desires. Trapped between fear and fascination, Leslie enters a world of seduction, control, and forbidden love. HolyTagsYo: Lesbian Romance, Sapphic Romance, GL (Girls Love).",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Chained by Her Love/en/2025-07-21 10:25:15/cover.webp",
            "video_url": "/video/b6a5c9e8-cef9-414b-94ca-5786ea0adc77",
            "total_episodes": 62,
            "rating": "9.22",
            "view": 49765,
            "likes": 577245
          },
          {
			"id": 706,
            "title": "To Serve and Submit",
			      "description": "Alicia needs a job to save her mom’s life. But when she enters the world of high fashion, she meets a powerful man with dark desires and dangerous rules. To survive, she must decide how far she’s willing to go.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/To Serve and Submit/en/2025-05-02 07:59:43/cover.webp",
            "video_url": "/video/9f631b5f-b6e1-495a-9b11-aa099459709d",
            "total_episodes": 10,
            "rating": "9.02",
            "view": 0,
            "likes": 15433
          },
          {
			"id": 707,
            "title": "Echoes of Vengeance",
			    "description": "In the world of high fashion, Elara plots revenge on Damon but soon discovers hidden depths to him that make her question everything.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Echoes of Vengeance/en/2024-10-15 11:47:25/cover.webp",
            "video_url": "/video/490c7782-e8ac-43a5-bcae-41d97e696692",
            "total_episodes": 50,
            "rating": "8.98",
            "view": 18317,
            "likes": 2483
          },
          {
			"id": 708,
            "title": "Forbidden Touch: My Vampire CEO",
			      "description": "Amanda is stuck in a toxic marriage until she discovers her boss is a vampire. He can’t touch her without pain, but when he saves her from her cruel husband, dangerous feelings spark between them.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Forbidden Touch: My Vampire CEO/en/2025-09-27 07:29:27/cover.webp",
            "video_url": "/video/b313b688-2fc8-4afb-b485-acddd09393be",
            "total_episodes": 43,
            "rating": "9.12",
            "view": 0,
            "likes": 25911
          },
          {
			"id": 709,
            "title": "Hired to Obey",
			      "description": "Desperate for money, Emma takes a job with David Watkins—a powerful photographer with strict rules. Their chemistry is electric, but his world is dangerous: a vengeful ex, a deadly debt, and a contract that binds her in ways she never imagined. Can she escape, or is she already his?",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Hired to Obey/en/2025-04-15 09:05:09/cover.webp",
            "video_url": "/video/1548868e-4c57-4adf-93d1-5620068450b0",
            "total_episodes": 58,
            "rating": "9.21",
            "view": 0,
            "likes": 265855
          },
          {
			"id": 710,
            "title": "My Billionaire Boss Claimed Me as His Prize",
			      "description": "Ivy needs a job to get her niece back. Zach, her cold and arrogant boss, wants to break her. But the more they fight, the harder they fall.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/ My Billionaire Boss Claimed Me as His Prize/en/2025-07-16 08:45:19/cover.webp",
            "video_url": "/video/e3aff0f8-7399-4246-9c86-b09fb4b617bb",
            "total_episodes": 73,
            "rating": "9.76",
            "view": 32432,
            "likes": 525971
          },
          {
			"id": 711,
            "title": "My Homeless Friend Turned Out to Be My CEO",
			      "description": "Sophie, a kind-hearted assistant, helps a homeless man—unaware he's actually her cold and arrogant CEO in disguise. As secrets unravel and emotions grow, both their lives are changed forever.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My Homeless Friend Turned Out to Be My CEO/en/2025-07-31 16:07:38/cover.webp",
            "video_url": "/video/02ba94ba-ea15-4f4b-9f5c-2b052e950635",
            "total_episodes": 89,
            "rating": "8.78",
            "view": 189345,
            "likes": 13546
          },
          {
			"id": 712,
            "title": "Secret Romance with BF's Dad",
			      "description": "A poor but proud girl is betrayed by her rich boyfriend — and finds unexpected love and protection in the arms of his father. What starts as heartbreak turns into a forbidden romance between two souls who shouldn’t fall for each other… but can’t resist.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Secret Romance with BF’s Dad/en/2025-11-11 16:47:52/cover.webp",
            "video_url": "/video/ac3d5c54-7e9c-4e52-80b3-153c1a425cb6",
            "total_episodes": 44,
            "rating": "9.09",
            "view": 21321,
            "likes": 23240
          },
          {
			"id": 713,
            "title": "Secrets of the Amnesia Billionaire",
			    "description": "When Sofia’s shop is threatened by the ruthless Denaro family, she rescues an amnesiac stranger who may be tied to them. Now, she must decide if she can trust him—or risk losing everything.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Secrets+of+the+Amnesia+Billionaire/en/2024-09-25+09:29:48/cover.webp",
            "video_url": "/video/7a3fbe78-134c-4ca1-950c-7dd066f4d048",
            "total_episodes": 63,
            "rating": "7.65",
            "view": 17953,
            "likes": 11039
          },
          {
			"id": 714,
            "title": "In Love With My Godfather's Daughter",
			    "description": "At 25, Luca Marcini becomes the new don after losing his father and brothers in a crash. Seen as weak, he’s determined to prove everyone wrong.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/In Love With My Godfather's Daughter/en/2024-10-15 11:49:05/cover.webp",
            "video_url": "/video/a739f74f-30d9-4804-8880-c94ee97f2f7c",
            "total_episodes": 50,
            "rating": "9.75",
            "view": 18622,
            "likes": 2848
          },
          {
			"id": 715,
            "title": "Love in the Shadows",
			    "description": "Sophie marries a stranger to save her family—only to discover he’s Robert Kensington, a ruthless CEO with deadly enemies. Trapped in a world of power, betrayal, and passion, she must risk everything.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Love in the Shadows/en/2025-01-22 15:48:07/cover.webp",
            "video_url": "/video/49ec1451-3e2d-4b42-8b65-64412cff8f69",
            "total_episodes": 51,
            "rating": "8.45",
            "view": 0,
            "likes": 30572
          },
          {
			"id": 716,
            "title": "Billionaire's Love in Danger",
			    "description": "Emily is saved by Luke after her drink is drugged. One night leads to a pregnancy, and she must decide whether to tell Luke, a wealthy heir, or keep it secret.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Billionaire's Love in Danger/en/2024-10-23 13:07:55/cover.webp",
            "video_url": "/video/b4e1ec51-e969-4f40-afeb-05c46f9fa9b7",
            "total_episodes": 70,
            "rating": "8.24",
            "view": 0,
            "likes": 17547
          }
          ]
        },		
      {
        "name": "Secret",
        "icon": "secret",
        "dramas": [
          {
			"id": 501,  
            "title": "Maid to Love Him",
			"description": "Julia, a cleaning lady who was sent back to Mexico, hates the spoiled musician Andy Rubio, a guest at her hotel. Andy pretends to be \"Santi,\" a simple maintenance worker, and Julia falls for him, not knowing his real identity. Their secret love forces Andy to choose between being a star and being his true self.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Maid to Love Him/en/2025-12-23 12:57:00/cover.webp",
            "video_url": "/video/cb556997-4437-45ee-acaf-0bb2c598ef17",
            "total_episodes": 71,
            "rating": "9.09",
            "view": 43453,
            "likes": 34545
          },
          {
			"id": 506,
            "title": "High-Stakes Heartthrob",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/High-Stakes Heartthrob/en/2025-11-24 11:41:01/cover.webp",
            "video_url": "/video/c0cf4598-a181-4301-a7dc-4d08e685bcaf",
            "total_episodes": 70,
            "rating": "9.65",
            "view": 45654,
            "likes": 29608
          },
          {
			"id": 508,
            "title": "Scarlet Seduction",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Scarlet Seduction/en/2025-10-22 09:05:28/cover.webp",
            "video_url": "/video/9e7851bd-3a74-4c2c-899a-71a1e58ab17e",
            "total_episodes": 79,
            "rating": "9.67",
            "view": 45657,
            "likes": 539031
          },
          {
			"id": 509,
            "title": "My Dark lord",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My Dark Lord/en/2025-09-24 09:39:08/cover.webp",
            "video_url": "/video/c9de114c-81a0-4d63-93dd-9db18249759f",
            "total_episodes": 78,
            "rating": "8.98",
            "view": 12432,
            "likes": 14706
          },
          {
			"id": 510,
            "title": "Stolen by Italian Macho",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Stolen by Italian Macho/en/2025-10-16 15:08:21/cover.webp",
            "video_url": "/video/4485df02-2712-4e96-9f6d-0097aa815060",
            "total_episodes": 43,
            "rating": "8.87",
            "view": 14321,
            "likes": 16630
          },
          {
			"id": 511,
            "title": "Mission: To Love the Enemy",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Mission: To Love the Enemy/en/2025-04-24 09:28:57/cover.webp",
            "video_url": "/video/f9d334e5-2036-4839-971d-e249684c1c3d",
            "total_episodes": 71,
            "rating": "8.98",
            "view": 29876,
            "likes": 17833
          },
          {
			"id": 512,
            "title": "Pregnant Cinderella",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Pregnant Cinderella/en/2025-09-27 07:38:02/cover.webp",
            "video_url": "/video/93cb920d-e852-4176-8176-9a39587cae61",
            "total_episodes": 50,
            "rating": "8.97",
            "view": 23454,
            "likes": 19875
          },
          {
			"id": 513,
            "title": "Hidden Detective Falls for Criminal",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Hidden Detective Falls for Criminal/en/2025-09-17 09:37:41/cover.webp",
            "video_url": "/video/97e1c006-b633-45c3-98d9-a33080b7f74a",
            "total_episodes": 71,
            "rating": "9.07",
            "view": 18906,
            "likes": 310483
          },
          {
			"id": 514,
            "title": "Wicked Game",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Wicked Game/en/2025-12-16 16:46:48/cover.webp",
            "video_url": "/video/db393286-7538-4d7b-9610-22a0dd465d78",
            "total_episodes": 79,
            "rating": "9.87",
            "view": 89765,
            "likes": 539496
          },
          {
			"id": 515,
            "title": "Trained to Tempt. Broken by Love",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Trained to Tempt. Broken by Love/en/2025-08-20 13:31:57/cover.webp",
            "video_url": "/video/6ffbb5de-27e4-40d5-97e6-a2cbfcbed6e4",
            "total_episodes": 68,
            "rating": "8.98",
            "view": 7656,
            "likes": 21602
          },
          {
			"id": 516,
            "title": "After All That Hurt",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/After All That Hurt/en/2025-08-20 12:42:01/cover.webp",
            "video_url": "/video/68ad507c-6cbb-45cd-a2fd-6f599ceec676",
            "total_episodes": 59,
            "rating": "9.25",
            "view": 45674,
            "likes": 27057
          },
          {
			"id": 517,
            "title": "Tss... He's Not My Husband. He's My Target",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Tss... He’s Not My Husband. He is My Target/en/2025-09-03 10:32:58/cover.webp",
			"description": "Two secret agents, Chloe and John, must pretend to be a married couple to stop a terrorist deal. But their growing feelings for each other put the whole mission in danger.",
            "video_url": "/video/5a9ddbf8-3caf-4ace-ba2a-1ded192915d2",
            "total_episodes": 80,
            "rating": "9.56",
            "view": 58907,
            "likes": 35656
          },
          {
			"id": 518,
            "title": "Ice and Flame",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Ice and Flame/en/2025-08-13 14:32:11/cover.webp",
			"description": "Emma, an ambitious undercover journalist, must infiltrate the world of hockey star Dylan Grayson to dig up a scandal that could save her career. But as she gets closer to him, sparks fly and secrets surface, putting both their futures at risk.",
            "video_url": "/video/fea468c8-ea42-43a7-a8f3-0a7e267b1d5c",
            "total_episodes": 72,
            "rating": "9.54",
            "view": 45321,
            "likes": 540447
          },
          {
			"id": 520,
            "title": "Confession",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Confession/en/2025-03-26 08:32:48/cover.webp",
			"description": "A devoted priest meets a woman from his past, awakening a dangerous desire. Torn between duty and love, he must choose—faith or passion? But secrets can destroy everything. Will he resist… or fall?",
            "video_url": "/video/5af63647-8fdf-4295-bcd1-066b2f7b5347",
            "total_episodes": 58,
            "rating": "8.87",
            "view": 1700000,
            "likes": 260824
          },
          {
			"id": 521,
            "title": "The Billionaire's Pretend Bride",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Billionaire’s Pretend Bride/en/2025-01-09 14:46:31/cover.webp",
			"description": "Emma hires billionaire Oliver to pose as her husband—but their fake romance turns real. As love sparks, a haunting curse and Oliver’s secret threaten to tear them apart. Who is the man in her dreams, and can love defy fate?",
            "video_url": "/video/5352606b-db59-4794-b68f-76f2f3cb83c7",
            "total_episodes": 95,
            "rating": "8.78",
            "view": 52700,
            "likes": 7876
          },
          {
			"id": 522,
            "title": "The Most Hated She-Wolf",
			"description": "Vanessa’s life falls apart when her husband, Alex, betrays her, kills her father, and takes the Alpha throne. Pregnant and in danger, she must fight to take back her power and protect her baby.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Most Hated She-Wolf/en/2024-10-15 12:20:02/cover.webp",
            "video_url": "/video/fc62993d-b7cd-4f10-b39d-7dedfc91d951",
            "total_episodes": 60,
            "rating": "9.54",
            "view": 14446,
            "likes": 6950
          },
          {
			"id": 523,
            "title": "Tied by Fate",
			"description": "Mia and James enter a contract marriage to solve their problems, but pretending to be in love leads to unexpected feelings, secrets, and sparks.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Tied by Fate/en/2024-10-15 12:01:38/cover.webp",
            "video_url": "/video/da81f83b-8666-4518-8aa5-493534a9b097",
            "total_episodes": 49,
            "rating": "8.82",
            "view": 14619,
            "likes": 2402
          },
          {
			"id": 524,
            "title": "The Dumb Billionaire Heiress In Love Part I",
			"description": "After inheriting her father’s fortune, Isabella faces enemies and scandal when her stepmother is found dead on her wedding day. Can the detective solve the case?",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Dumb Billionaire Heiress In Love Part I/en/2024-10-15 12:00:23/cover.webp",
            "video_url": "/video/c356998e-b8e0-4206-8949-0e78dd3c4e82",
            "total_episodes": 50,
            "rating": "7.92",
            "view": 17683,
            "likes": 3578
          },
          {
			"id": 525,
            "title": "The Dumb Billionaire Heiress In Love Part II",
			"description": "After losing her memory, Isabella learns Stella stole her identity. With Antonio’s help, she uncovers the truth—she was never in a coma.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Dumb Billionaire Heiress In Love Part II/en/2024-10-15 12:00:45/cover.webp",
            "video_url": "/video/00f23275-566f-4a4a-9cea-6640f4f7dffa",
            "total_episodes": 50,
            "rating": "7.81",
            "view": 16822,
            "likes": 1119
          },
          {
			"id": 526,
            "title": "Got Myself A Priceless Husband",
			"description": "Billionaire Norman Miles needs a wife to save his inheritance. Fiercely independent Beth needs protection from a sleazy client. His deal: no strings attached. But neither expected it to feel so real.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Got Myself A Priceless Husband/en/2024-11-12 11:02:56/cover.webp",
            "video_url": "/video/f5cb8ed1-a50b-48e6-9471-2574444c1942",
            "total_episodes": 55,
            "rating": "8.99",
            "view": 46500,
            "likes": 6952
          },
          {
			"id": 527,
            "title": "My Handsome Bodyguard",
			"description": "Lea, the spoiled daughter of a mafia boss, takes over the business after her father’s tragic death. With her new bodyguard Ryan’s advice, she calls off her engagement to Michael—who storms off, vowing revenge.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My Handsome Bodyguard/en/2024-10-15 11:57:08/cover.webp",
            "video_url": "/video/b9bd7e2a-91ed-46fb-9d54-7c57a49f7061",
            "total_episodes": 50,
            "rating": "9.24",
            "view": 13669,
            "likes": 7144
          },
          {
			"id": 528,
            "title": "Hollywood Star's Fake Girlfriend",
			"description": "Star actor Ben fakes a romance with Blake to squash damaging rumors, but when his jealous ex, Ria, uncovers the truth, she unleashes a scandal that could destroy him.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Hollywood Star's Fake Girlfriend/en/2024-10-15 11:47:54/cover.webp",
            "video_url": "/video/7f9421b7-2a28-4d46-9e25-39751400aeab",
            "total_episodes": 50,
            "rating": "8.16",
            "view": 16371,
            "likes": 3634
          },
          {
			"id": 529,
            "title": "Alpha Accidentally Got Me Pregnant",
			"description": "Aria goes to the hospital for a check-up but is accidentally inseminated with a powerful Alpha werewolf’s baby. Now trapped in a dangerous world, she must fight to protect herself and the life growing inside her.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Alpha Accidentally Got Me Pregnant/en/2025-08-12 14:24:41/cover.webp",
            "video_url": "/video/a42a8be4-7d30-4a19-840d-ca0ac8220482",
            "total_episodes": 71,
            "rating": "8.90",
            "view": 19765,
            "likes": 12845
          },
          {
			"id": 530,
            "title": "Mafia Lord's Son Has Secret Love For His Stepmom",
			"description": "When Bianca’s father seeks revenge with help from Mafia Lord Don Pedro, she’s left as collateral. Enzo, Don Pedro’s son, falls for her and teams up with Bianca to plot their escape.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Mafia Lord's Son Has Secret Love For His Stepmom/en/2024-10-15 11:49:30/cover.webp",
            "video_url": "/video/0bda8c34-3bc6-4764-abcf-f2c045d8e71c",
            "total_episodes": 51,
            "rating": "9.33",
            "view": 14641,
            "likes": 1382
          },
          {
			"id": 531,
            "title": "My Evil Sister's Fiancé is My Mate",
			"description": "When shy and mistreated Emily accidentally ruins her sister's engagement party, she discovers a shocking secret — her sister’s fiancé is her fated mate. Now bound by a supernatural thread, Emily and the powerful alpha Drake must fight their growing desire and the wrath of her evil family.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My Evil Sister’s Fiancé is My Mate/en/2025-08-05 15:46:57/cover.webp",
            "video_url": "/video/34bc37e3-212a-47d8-9b75-3580301bfb8e",
            "total_episodes": 71,
            "rating": "8.98",
            "view": 12324,
            "likes": 8452
          },
          {
			"id": 532,
            "title": "Never Mess With A Badass Girl",
			"description": "Betrayed by her boyfriend and best friend, Ava teams up with Max for a ruthless revenge plan. Will Ava’s revenge bring closure—or cost her everything?",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Never Mess With A Badass Girl/en/2024-10-15 11:57:59/cover.webp",
            "video_url": "/video/381c8776-7723-437d-ae86-c6fc3e908d21",
            "total_episodes": 50,
            "rating": "7.73",
            "view": 13748,
            "likes": 929
          },
          {
			"id": 533,
            "title": "My Celebrity Boyfriend Killed Me",
			"description": "Actress Claire is betrayed by producer Mitch when she gets pregnant. Twenty years later, her daughter and Alex, her savior, are ready for revenge.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My Celebrity Boyfriend Killed Me/en/2024-10-15 11:50:00/cover.webp",
            "video_url": "/video/2a2344a8-e03e-485e-ae30-57a401383297",
            "total_episodes": 41,
            "rating": "8.74",
            "view": 17524,
            "likes": 2865
          },
          {
			"id": 534,
            "title": "The Phoenix Conspiracy",
			"description": "A private investigator falls into the dangerous web of a secret group led by the mysterious Phoenix after getting involved with a woman tied to the organization.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Phoenix Conspiracy/en/2024-10-15 12:01:09/cover.webp",
            "video_url": "/video/4ad399aa-e552-4369-b657-f93e953760d3",
            "total_episodes": 48,
            "rating": "9.75",
            "view": 16259,
            "likes": 2369
          },
          {
			"id": 535,
            "title": "The Little Sister Isn't What She Seems",
			"description": "Li Zhi, a struggling cartoonist, and Wang Da Yu, a star gamer, become housemates. As they grow closer, their landlady hilariously misreads their friendship. Miss, it’s not what you think!",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Little Sister Isn't What She Seems/en/2024-10-15 16:03:51/cover.webp",
            "video_url": "/video/d311ef12-6445-49db-9544-365bf74d09bb",
            "total_episodes": 119,
            "rating": "7.60",
            "view": 16930,
            "likes": 1083
          },
          {
			"id": 536,
            "title": "Mute Girl Loved the Man Sent to Betray Her",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Mute Girl Loved the Man Sent to Betray Her/en/2025-07-16 08:26:32/cover.webp",
            "video_url": "/video/3cd0380b-9b49-4420-bd65-76a8b3ab1041",
            "total_episodes": 75,
            "rating": "9.09",
            "view": 12342,
            "likes": 11000
          },
          {
			"id": 537,
            "title": "Rosy Psycho",
			"description": "Gigi Philips, a sweet and loving ray of sunshine, is the heir to ROSE Beauty, her parents’ cosmetics empire. But as she’s groomed to take over, secrets and betrayal threaten to shatter her perfect world.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Rosy Psycho/en/2024-10-15 11:59:06/cover.webp",
            "video_url": "/video/b6c525c6-c9a2-4696-818e-33981e27e123",
            "total_episodes": 49,
            "rating": "8.46",
            "view": 18582,
            "likes": 2098
          },
          {
			"id": 539,
            "title": "School Hall",
			"description": "At Crestwood Academy, four friends search for a missing classmate, uncovering dark secrets, sinister rituals, and chilling truths that test their courage.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/School Hall/en/2024-10-15 11:59:27/cover.webp",
            "video_url": "/video/889c78f5-d1a2-488d-b455-5efa6b33af09",
            "total_episodes": 49,
            "rating": "9.57",
            "view": 13892,
            "likes": 1789
          },
          {
			"id": 540,
            "title": "I Died and Woke Up as a TV Show Villain",
			"description": "An actress dies on set and wakes up in the body of the villain she used to play — Tess Hart, the scandalous wife of a dangerous duke. Now trapped in a deadly romance, she must survive a world where love can kill and her.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/I Died and Woke Up as a TV Show Villain/en/2025-08-01 07:58:38/cover.webp",
            "video_url": "/video/d1eb2700-536c-4a74-b6b4-68f54c2d6dfc",
            "total_episodes": 76,
            "rating": "8.90",
            "view": 23876,
            "likes": 12323
          },
          {
			"id": 542,
            "title": "Who am I?",
			"description": "An ordinary guy’s boring morning turns into chaos when assassins start shooting at him — and a stranger who looks exactly like him drags him into a deadly chase. Now George must figure out who this double is, and why everyone wants him dead before he loses his identity — and his life.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Who am I/en/2025-10-21 10:42:14/cover.webp",
            "video_url": "/video/0922eabf-99e5-4189-b749-2cd1d2cf686f",
            "total_episodes": 69,
            "rating": "8.07",
            "view": 45000,
            "likes": 6754
          },
          {
			"id": 545,
            "title": "The Billionaire's Pick: Perfect Match",
			"description": "A heartbroken billionaire, betrayed by his fiancée, decides to choose his next “bride” in the most unusual way — by her hands. When a poor immigrant girl accidentally falls into his arms, his revenge plan turns into an unexpected romance that neither of them saw coming.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Billionaire’s Pick: Perfect Match/en/2025-10-24 13:05:07/cover.webp",
            "video_url": "/video/d02d42b3-7686-4f2c-be78-26b08f41ef14",
            "total_episodes": 56,
            "rating": "8.90",
            "view": 20213,
            "likes": 16143
          }
        ]
      },
      {
        "name": "Asian",
        "icon": "asian",
        "dramas": [
          {
			"id": 601,
            "title": "From Outcast to Heir",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/From Outcast to Heir/en/2024-10-15 16:05:16/cover.webp",
			"description": "A one-night stand left Ms. Qin with a secret son. Years later, the father returns to protect them from a looming threat—but will he bring safety or more danger?",
            "video_url": "/video/a6c10ec1-73a5-43b7-b3bf-cbc307c1dce2",
            "total_episodes": 60,
            "rating": "8.18",
            "view": 15335,
            "likes": 3915
          },
          {
			"id": 602,
            "title": "Delivering Love",
			"description": "On her wedding eve, Gu Anran catches her fiancé cheating and marries a beggar in anger—only to discover he’s a billionaire CEO!",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Delivering Love/en/2024-10-15 12:02:50/cover.webp",
            "video_url": "/video/cc23c814-1bf6-464f-8e83-29c093f46b05",
            "total_episodes": 80,
            "rating": "8.96",
            "view": 17487,
            "likes": 3100
          },
          {
			"id": 603,
            "title": "Secret Baby for the CEO",
			"description": "My billionaire lover sent me to prison for a crime I didn’t commit. After years of torment, I’m back—and this time, I won’t forgive him!",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Secret Baby for the CEO/en/2024-10-15 12:03:50/cover.webp",
            "video_url": "/video/8017f822-c4ea-4dd4-92fb-0098ca9101dc",
            "total_episodes": 79,
            "rating": "9.08",
            "view": 16951,
            "likes": 3244
          },
          {
			"id": 604,
            "title": "It's Obvious that You Love Me",
			"description": "Six years after losing her son and being shunned by the Li family, Xiaoxi returns to reclaim her child, seek revenge, and uncover the truth about her grandfather’s death.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/It’s Obvious that You Love Me/en/2024-10-15 16:04:24/cover.webp",
            "video_url": "/video/029dcb8e-44f3-4782-8eb6-3bd7f401c723",
            "total_episodes": 139,
            "rating": "9.39",
            "view": 15786,
            "likes": 4785
          },
          {
			"id": 605,
            "title": "The Cold CEO's Cure",
			"description": "Shunned after her mother’s death, the Su heiress marries into the Li clan for answers. But meeting Li Shao sparks a mysterious, familiar attraction...",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Cold CEO’s Cure/en/2024-10-15 12:04:32/cover.webp",
            "video_url": "/video/5451fabb-91ea-491b-a847-8d65f0a324e6",
            "total_episodes": 80,
            "rating": "8.65",
            "view": 13539,
            "likes": 3476
          },
          {
			"id": 606,
            "title": "My husband is a Secret CEO",
			"description": "Gu Anran’s wedding is ruined when she catches her sister with her fiancé. Heartbroken, she flees and accidentally knocks out a stranger—billionaire Lu Mingyuan, who will soon change her life forever.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My husband is a Secret CEO/en/2024-10-15 12:03:31/cover.webp",
            "video_url": "/video/ed66838d-fc43-404e-84e1-96c4c19b10d4",
            "total_episodes": 80,
            "rating": "8.67",
            "view": 14604,
            "likes": 2258
          },
          {
			"id": 607,
            "title": "The Fight for Everything Lost",
			"description": "In a world where love is fragile and betrayal leaves no forgiveness, a story of passion, pain, and broken vows unfolds. When the truth comes to light, the heroine chooses the path of revenge—her only salvation and her greatest curse.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/The Fight for Everything Lost/en/2024-10-15 16:05:41/cover.webp",
            "video_url": "/video/0a6a31ad-5cc7-4e70-9b8d-6f79b8c0041e",
            "total_episodes": 100,
            "rating": "8.77",
            "view": 14355,
            "likes": 1897
          },
          {
			"id": 608,
            "title": "My Favorite Savior",
			"description": "Yiyi saves a stranger, only to find he’s her fiancé in a 99-day marriage deal for a rare herb and revenge. But Fu Yanting knows her secret.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My Favorite Savior/en/2024-10-15 12:03:11/cover.webp",
            "video_url": "/video/fbbb640b-b01a-4f14-a47d-d87202793404",
            "total_episodes": 81,
            "rating": "7.60",
            "view": 17851,
            "likes": 2037
          },
          {
			"id": 609,
            "title": "Sweet Revenge",
			"description": "Six years after losing everything, Mo Ning Xi returns as a famous designer to reclaim her life and face her scheming rival—with help from her still-loving ex-husband.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Sweet Revenge/en/2024-10-15 12:04:08/cover.webp",
            "video_url": "/video/8e57ef8f-0c7e-4668-b6e1-11920f654ce5",
            "total_episodes": 80,
            "rating": "9.16",
            "view": 16495,
            "likes": 1693
          },
          {
			"id": 610,
            "title": "My Sweetheart Forever",
			"description": "Xu Nian hides her sharp mind to uncover her mother’s death. CEO Gu Ming Yan suspects her family in his father’s murder. Bound by a fake marriage, their battle turns to love as they face a bigger enemy.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/My Sweetheart Forever/en/2024-10-15 16:04:56/cover.webp",
            "video_url": "/video/1994b976-b8aa-46d5-b40f-4ed90c0e6b66",
            "total_episodes": 19,
            "rating": "9.31",
            "view": 14135,
            "likes": 1512
          },
          {
			"id": 611,
            "title": "Starting Over: 1990",
			"description": "Drowning in guilt after losing his family, Jaxton wakes up in 1990, where his wife and daughter are alive. Given a second chance, he must fix his mistakes—or risk losing them again.",
            "portrait": "https://dj0vkl2i4vsbo.cloudfront.net/convert/Starting Over: 1990/en/2024-10-15 16:03:25/cover.webp",
            "video_url": "/video/7e041d53-62e7-4999-ace2-73a8d7ce416e",
            "total_episodes": 60,
            "rating": "9.32",
            "view": 15632,
            "likes": 2184
          }
        ]
      },
      ]
    };

    setTimeout(() => {
      setData(mockData);
      setLoading(false);
      
      if (currentRoute) {
        const allDramas = mockData.sections.flatMap(s => s.dramas);
        const drama = allDramas.find(d => d.id === currentRoute);
        if (drama) {
          const lastWatched = JSON.parse(localStorage.getItem('lastWatched') || '{}');
          handlePlay(drama, lastWatched[drama.id]?.episode || 1);
        }
      }
    }, 500);
  }, [currentRoute]);

  const handlePlay = (drama, episode = 1) => {
    setSelectedDrama(drama);
    setSelectedEpisode(episode);
    window.history.pushState({}, '', `?id=${drama.id}`);
  };

  const handleClose = () => {
    setSelectedDrama(null);
    window.history.pushState({}, '', window.location.pathname);
  };

  const getAllDramas = () => {
    if (!data) return [];
    const allDramas = data.sections.flatMap(section => section.dramas);
    const uniqueDramas = allDramas.filter((drama, index, self) =>
      index === self.findIndex((d) => d.id === drama.id)
    );
    return uniqueDramas;
  };

  const getSearchResults = () => {
    if (!searchQuery) return [];
    const allDramas = getAllDramas();
    return allDramas.filter(drama => 
      drama.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getIconForSection = (iconName) => {
    switch (iconName) {
      case 'crown': return <Crown className="w-6 h-6 text-yellow-400" />;
      case 'sparkles': return <Sparkles className="w-6 h-6 text-purple-400" />;
      case 'trending': return <TrendingUp className="w-6 h-6 text-pink-400" />;
      case 'vip': return <Gem className="w-6 h-6 text-amber-400" />;
      case 'best': return <ThumbsUp className="w-6 h-6 text-green-400" />;
	  case 'secret': return <Sparkles className="w-6 h-6 text-rose-400" />;
	  case 'asian': return <Sparkles className="w-6 h-6 text-orange-400" />
	  case 'billionaire': return <Sparkles className="w-6 h-6 text-yellow-400" />
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Loading dramas...</p>
        </div>
      </div>
    );
  }

  const featuredSection = data?.sections.find(s => s.name === "Featured");
  const featuredDramas = featuredSection?.dramas || [];
  const searchResults = getSearchResults();  

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-black">
      {selectedDrama && (
        <DramaPlayer 
          drama={selectedDrama} 
          onClose={handleClose}
          initialEpisode={selectedEpisode}
        />
      )}

      <header className="sticky top-0 z-40 bg-black bg-opacity-70 backdrop-blur-lg border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
              <Play className="w-6 h-6 text-white" fill="white" />
            </div>
            <span className="text-xl md:text-2xl font-bold text-white">MY DRAMA FANDOM</span>
          </div>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setShowSearch(!showSearch)}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <Search className="w-5 h-5 text-white" />
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <Home className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
        
        {showSearch && (
          <div className="max-w-7xl mx-auto px-4 pb-4">
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search dramas..."
              className="w-full bg-white/10 border border-white/20 rounded-full px-6 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600"
            />

            {searchQuery && searchResults.length > 0 && (
              <div className="mt-3 bg-black/90 rounded-2xl p-4 max-h-96 overflow-y-auto">
                {searchResults.map(drama => (
                  <div 
                    key={drama.id}
                    onClick={() => {
                      handlePlay(drama);
                      setShowSearch(false);
                      setSearchQuery('');
                    }}
                    className="flex items-center gap-4 p-3 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
                  >
                    <img src={drama.portrait} alt={drama.title} className="w-14 h-20 object-cover rounded-md" />
                    <div>
                      <h3 className="text-white font-semibold">{drama.title}</h3>
                      <p className="text-gray-400 text-sm">{drama.total_episodes} Episodes</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

<main className="max-w-7xl mx-auto py-8">

  {/* Hide everything except the expanded section when View All is active */}
  {expandedSection === null && (
    <>
      {featuredDramas.length > 0 && (
        <HeroSection featuredDramas={featuredDramas} onPlay={handlePlay} />
      )}
      <ContinueWatchingSection dramas={getAllDramas()} onPlay={handlePlay} />
    </>
  )}

  {data?.sections
    .filter(s => s.name !== "Featured" && s.name !== "More Series")
    .map((section, index) => (
      <CategorySection
        key={index}
        categoryName={section.name}
        dramas={section.dramas}
        onPlay={handlePlay}
        icon={getIconForSection(section.icon)}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
      />
    ))}

  {expandedSection === null && (
    <MoreSeriesGrid dramas={getAllDramas()} onPlay={handlePlay} />
  )}

</main>

{expandedSection === null && (
  <footer className="border-t border-gray-800 mt-20 py-12 text-center text-gray-400">
    <p>© 2026 My Drama Fandom</p>
  </footer>
)}

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes pulse-ring {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s ease-in-out infinite;
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}